import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { Plus, X, Clock, Search } from 'lucide-react';
import cityTimezoneMap from './cities.js';
import { useTranslation } from 'react-i18next';
import './i18n.js';

// Ad Banner Component - Supports Google AdSense and Carbon Ads
const AdBanner = ({ type = 'adsense', slot, format = 'horizontal', onClose, show = true, isMobile = false, theme = 'dark', themeColors = {} }) => {
  const [adLoaded, setAdLoaded] = useState(false);
  const [adFailed, setAdFailed] = useState(false);
  const adInitializedRef = useRef(false);

  useEffect(() => {
    // Only initialize ads once, never retry
    if (adInitializedRef.current || !show) return;

    adInitializedRef.current = true;

    if (type === 'adsense' && typeof window !== 'undefined') {
      // Check if AdSense script is available
      if (!window.adsbygoogle) {
        // AdSense not available, fail immediately
        setAdFailed(true);
        return;
      }

      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});

        // Check if ad loaded after 3 seconds
        const timer = setTimeout(() => {
          const adContainer = document.querySelector('.adsbygoogle[data-ad-slot="' + slot + '"]');
          if (adContainer) {
            const hasContent = adContainer.children.length > 0 || adContainer.innerHTML.trim().length > 0;
            if (hasContent) {
              setAdLoaded(true);
            } else {
              setAdFailed(true);
            }
          } else {
            setAdFailed(true);
          }
        }, 3000);

        return () => clearTimeout(timer);
      } catch (err) {
        console.error('AdSense error:', err);
        setAdFailed(true);
      }
    } else if (type === 'carbon' && typeof window !== 'undefined') {
      // Check if script already exists
      if (document.getElementById('_carbonads_js')) {
        setAdFailed(true);
        return;
      }

      // Carbon Ads initialization
      const script = document.createElement('script');
      script.src = '//cdn.carbonads.com/carbon.js?serve=your-carbon-id&placement=yoursite';
      script.id = '_carbonads_js';
      script.async = true;

      // Fallback timeout - if ad doesn't load within 5 seconds, consider it failed
      const fallbackTimer = setTimeout(() => {
        const carbonContainer = document.getElementById('carbon-ads-container');
        if (!carbonContainer || carbonContainer.children.length === 0) {
          setAdFailed(true);
        }
      }, 5000);

      script.onload = () => {
        // Check if Carbon ad loaded after 1 second
        setTimeout(() => {
          const carbonContainer = document.getElementById('carbon-ads-container');
          if (carbonContainer && carbonContainer.children.length > 0) {
            setAdLoaded(true);
            clearTimeout(fallbackTimer);
          } else {
            setAdFailed(true);
            clearTimeout(fallbackTimer);
          }
        }, 1000);
      };

      script.onerror = () => {
        setAdFailed(true);
        clearTimeout(fallbackTimer);
      };

      const container = document.getElementById('carbon-ads-container');
      if (container) {
        container.appendChild(script);
      } else {
        // Container doesn't exist, fail immediately
        setAdFailed(true);
        clearTimeout(fallbackTimer);
      }

      return () => {
        clearTimeout(fallbackTimer);
        // Cleanup script on unmount
        const existingScript = document.getElementById('_carbonads_js');
        if (existingScript) {
          existingScript.remove();
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]); // Only re-run if show changes, but ref prevents multiple initializations

  // Don't render if not showing or if ad failed to load
  if (!show || adFailed) return null;

  const adSizes = {
    horizontal: { width: '728px', height: '90px', mobileWidth: '320px', mobileHeight: '100px' },
    vertical: { width: '300px', height: '250px' },
    square: { width: '330px', height: '250px' }
  };

  const size = adSizes[format];

  return (
    <div style={{
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      padding: isMobile ? '0.5rem 1rem' : '1rem 2rem',
      position: 'relative',
      background: theme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)'
    }}>
      {onClose && (
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '0.5rem',
            right: isMobile ? '0.5rem' : '1rem',
            background: themeColors.panelBg,
            border: `1px solid ${themeColors.border}`,
            borderRadius: '0.375rem',
            padding: '0.375rem',
            fontSize: '0.875rem',
            color: themeColors.textMuted,
            cursor: 'pointer',
            fontFamily: "'Sora', sans-serif",
            zIndex: 10,
            transition: 'all 0.2s ease',
            minWidth: '28px',
            minHeight: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
            e.currentTarget.style.color = '#ef4444';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = themeColors.panelBg;
            e.currentTarget.style.color = themeColors.textMuted;
          }}
          title="Close ad"
        >
          ✕
        </button>
      )}
      <div style={{
        width: '100%',
        maxWidth: isMobile ? (size.mobileWidth || size.width) : size.width,
        minHeight: isMobile ? (size.mobileHeight || size.height) : size.height,
        background: themeColors.panelBg,
        border: `1px solid ${themeColors.border}`,
        borderRadius: '0.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {type === 'adsense' ? (
          <>
            {/* Google AdSense */}
            <ins
              className="adsbygoogle"
              style={{
                display: 'inline-block',
                width: isMobile ? (size.mobileWidth || size.width) : size.width,
                height: isMobile ? (size.mobileHeight || size.height) : size.height
              }}
              data-ad-client="ca-pub-XXXXXXXXXXXXXXXX"
              data-ad-slot={slot}
              data-ad-format={format === 'horizontal' ? 'horizontal' : 'rectangle'}
              data-full-width-responsive={isMobile ? 'true' : 'false'}
            />
          </>
        ) : (
          <>
            {/* Carbon Ads */}
            <div id="carbon-ads-container" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100px'
            }} />
          </>
        )}

        {/* Fallback placeholder */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          color: themeColors.textMuted,
          fontSize: '0.75rem',
          fontFamily: "'Sora', sans-serif",
          padding: '1rem',
          pointerEvents: 'none',
          zIndex: -1
        }}>
          <div>
            <div style={{ marginBottom: '0.5rem', opacity: 0.5, fontSize: '1.5rem' }}>📢</div>
            <div>Advertisement</div>
          </div>
        </div>
      </div>
    </div>
  );
};
const TimeZoneVisualizer = () => {
  const { t, i18n } = useTranslation();

  const [locations, setLocations] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('timezoneLocations');
        if (saved) {
          const parsedLocations = JSON.parse(saved);
          // Ensure at least one location is primary
          if (parsedLocations.length > 0 && !parsedLocations.some(loc => loc.isPrimary)) {
            parsedLocations[0].isPrimary = true;
          }
          // Ensure primary location is first
          const primaryLoc = parsedLocations.find(loc => loc.isPrimary);
          const otherLocs = parsedLocations.filter(loc => !loc.isPrimary);
          return primaryLoc ? [primaryLoc, ...otherLocs] : parsedLocations;
        }
      } catch (error) {
        console.error('Failed to load saved locations:', error);
      }
    }
    return [
      { id: 1, name: 'New York', timezone: 'America/New_York', color: '#2563eb', isPrimary: true, businessHourStart: 9, businessHourEnd: 17 },
      { id: 2, name: 'London', timezone: 'Europe/London', color: '#dc2626', isPrimary: false, businessHourStart: 9, businessHourEnd: 17 }
    ];
  });

  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [filteredCities, setFilteredCities] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'dark';
    }
    return 'dark';
  });
  const [editingHours, setEditingHours] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [swipedLocation, setSwipedLocation] = useState(null);
  const [swipeX, setSwipeX] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [showTopAd, setShowTopAd] = useState(true);
  const [showBottomAd, setShowBottomAd] = useState(true);
  const [pullDistance, setPullDistance] = useState(0);
  const [contextMenu, setContextMenu] = useState(null);
  const [favorites, setFavorites] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('favoriteTimezones');
      return saved ? JSON.parse(saved) : [];
    }
    return [];
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showBestTimes, setShowBestTimes] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('showBestTimes');
      return saved !== null ? JSON.parse(saved) : true;
    }
    return true;
  });
  const [bestMeetingTimes, setBestMeetingTimes] = useState([]);
  const [presets, setPresets] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('timezonePresets');
      return saved ? JSON.parse(saved) : [];
    }
    return [];
  });
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [timeFormat, setTimeFormat] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('timeFormat') || '24';
    }
    return '24';
  });
  const [undoStack, setUndoStack] = useState([]);
  const [tooltipLocation, setTooltipLocation] = useState(null);
  const [meetingDuration, setMeetingDuration] = useState(() => {
    if (typeof window !== 'undefined') {
      return parseInt(localStorage.getItem('meetingDuration') || '60');
    }
    return 60;
  });
  const [holidays, setHolidays] = useState({});

  // Comprehensive timezone to country code mapping for Nager.Date API
  const timezoneToCountryCode = useMemo(() => ({
    // North America
    'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US', 'America/Los_Angeles': 'US',
    'America/Phoenix': 'US', 'America/Anchorage': 'US', 'America/Honolulu': 'US', 'America/Detroit': 'US',
    'America/Indianapolis': 'US', 'America/Boise': 'US', 'America/Toronto': 'CA', 'America/Vancouver': 'CA',
    'America/Edmonton': 'CA', 'America/Winnipeg': 'CA', 'America/Halifax': 'CA', 'America/St_Johns': 'CA',
    'America/Mexico_City': 'MX', 'America/Cancun': 'MX', 'America/Monterrey': 'MX', 'America/Tijuana': 'MX',

    // South America
    'America/Sao_Paulo': 'BR', 'America/Rio_Branco': 'BR', 'America/Manaus': 'BR', 'America/Fortaleza': 'BR',
    'America/Buenos_Aires': 'AR', 'America/Santiago': 'CL', 'America/Lima': 'PE', 'America/Bogota': 'CO',
    'America/Caracas': 'VE', 'America/La_Paz': 'BO', 'America/Guayaquil': 'EC', 'America/Montevideo': 'UY',
    'America/Asuncion': 'PY', 'America/Cayenne': 'GF', 'America/Paramaribo': 'SR',

    // Europe - Western
    'Europe/London': 'GB', 'Europe/Dublin': 'IE', 'Europe/Lisbon': 'PT', 'Atlantic/Azores': 'PT',
    'Atlantic/Madeira': 'PT', 'Europe/Paris': 'FR', 'Europe/Brussels': 'BE', 'Europe/Amsterdam': 'NL',
    'Europe/Luxembourg': 'LU', 'Europe/Monaco': 'MC', 'Europe/Andorra': 'AD',

    // Europe - Central
    'Europe/Berlin': 'DE', 'Europe/Zurich': 'CH', 'Europe/Vienna': 'AT', 'Europe/Rome': 'IT',
    'Europe/Madrid': 'ES', 'Europe/Barcelona': 'ES', 'Europe/Oslo': 'NO', 'Europe/Stockholm': 'SE',
    'Europe/Copenhagen': 'DK', 'Europe/Prague': 'CZ', 'Europe/Warsaw': 'PL', 'Europe/Budapest': 'HU',
    'Europe/Bratislava': 'SK', 'Europe/Ljubljana': 'SI', 'Europe/Zagreb': 'HR', 'Europe/Belgrade': 'RS',
    'Europe/Sarajevo': 'BA', 'Europe/Podgorica': 'ME', 'Europe/Skopje': 'MK', 'Europe/Tirane': 'AL',

    // Europe - Eastern
    'Europe/Athens': 'GR', 'Europe/Bucharest': 'RO', 'Europe/Sofia': 'BG', 'Europe/Helsinki': 'FI',
    'Europe/Tallinn': 'EE', 'Europe/Riga': 'LV', 'Europe/Vilnius': 'LT', 'Europe/Kiev': 'UA',
    'Europe/Chisinau': 'MD', 'Europe/Minsk': 'BY', 'Europe/Moscow': 'RU', 'Europe/Kaliningrad': 'RU',
    'Europe/Samara': 'RU', 'Europe/Istanbul': 'TR', 'Asia/Nicosia': 'CY', 'Europe/Malta': 'MT',

    // Asia - Middle East
    'Asia/Dubai': 'AE', 'Asia/Riyadh': 'SA', 'Asia/Kuwait': 'KW', 'Asia/Qatar': 'QA', 'Asia/Bahrain': 'BH',
    'Asia/Muscat': 'OM', 'Asia/Jerusalem': 'IL', 'Asia/Amman': 'JO', 'Asia/Beirut': 'LB', 'Asia/Damascus': 'SY',
    'Asia/Baghdad': 'IQ', 'Asia/Tehran': 'IR', 'Asia/Yerevan': 'AM', 'Asia/Baku': 'AZ', 'Asia/Tbilisi': 'GE',

    // Asia - South
    'Asia/Karachi': 'PK', 'Asia/Kolkata': 'IN', 'Asia/Colombo': 'LK', 'Asia/Dhaka': 'BD', 'Asia/Kathmandu': 'NP',
    'Asia/Thimphu': 'BT', 'Asia/Kabul': 'AF', 'Indian/Maldives': 'MV',

    // Asia - Southeast
    'Asia/Bangkok': 'TH', 'Asia/Jakarta': 'ID', 'Asia/Singapore': 'SG', 'Asia/Kuala_Lumpur': 'MY',
    'Asia/Manila': 'PH', 'Asia/Ho_Chi_Minh': 'VN', 'Asia/Phnom_Penh': 'KH', 'Asia/Vientiane': 'LA',
    'Asia/Yangon': 'MM', 'Asia/Brunei': 'BN', 'Asia/Makassar': 'ID', 'Asia/Pontianak': 'ID',

    // Asia - East
    'Asia/Tokyo': 'JP', 'Asia/Seoul': 'KR', 'Asia/Shanghai': 'CN', 'Asia/Hong_Kong': 'HK',
    'Asia/Taipei': 'TW', 'Asia/Macau': 'MO', 'Asia/Ulaanbaatar': 'MN', 'Asia/Pyongyang': 'KP',
    'Asia/Urumqi': 'CN', 'Asia/Chongqing': 'CN',

    // Oceania
    'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Australia/Brisbane': 'AU', 'Australia/Perth': 'AU',
    'Australia/Adelaide': 'AU', 'Australia/Darwin': 'AU', 'Australia/Hobart': 'AU', 'Australia/Canberra': 'AU',
    'Pacific/Auckland': 'NZ', 'Pacific/Chatham': 'NZ', 'Pacific/Fiji': 'FJ', 'Pacific/Tongatapu': 'TO',
    'Pacific/Apia': 'WS', 'Pacific/Port_Moresby': 'PG', 'Pacific/Guadalcanal': 'SB', 'Pacific/Noumea': 'NC',
    'Pacific/Tahiti': 'PF', 'Pacific/Guam': 'GU', 'Pacific/Saipan': 'MP', 'Pacific/Palau': 'PW',

    // Africa
    'Africa/Cairo': 'EG', 'Africa/Johannesburg': 'ZA', 'Africa/Lagos': 'NG', 'Africa/Nairobi': 'KE',
    'Africa/Algiers': 'DZ', 'Africa/Casablanca': 'MA', 'Africa/Tunis': 'TN', 'Africa/Tripoli': 'LY',
    'Africa/Khartoum': 'SD', 'Africa/Addis_Ababa': 'ET', 'Africa/Dar_es_Salaam': 'TZ', 'Africa/Kampala': 'UG',
    'Africa/Lusaka': 'ZM', 'Africa/Harare': 'ZW', 'Africa/Maputo': 'MZ', 'Africa/Windhoek': 'NA',
    'Africa/Gaborone': 'BW', 'Africa/Kinshasa': 'CD', 'Africa/Luanda': 'AO', 'Africa/Accra': 'GH',
    'Africa/Dakar': 'SN', 'Africa/Abidjan': 'CI', 'Africa/Bamako': 'ML', 'Africa/Monrovia': 'LR',

    // Atlantic
    'Atlantic/Reykjavik': 'IS', 'Atlantic/Bermuda': 'BM', 'Atlantic/Cape_Verde': 'CV',
    'Atlantic/Stanley': 'FK', 'Atlantic/South_Georgia': 'GS'
  }), []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('timezoneLocations', JSON.stringify(locations));
      } catch (error) {
        console.error('Failed to save locations:', error);
      }
    }
  }, [locations]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('theme', theme);
    }
  }, [theme]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('favoriteTimezones', JSON.stringify(favorites));
    }
  }, [favorites]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('timezonePresets', JSON.stringify(presets));
    }
  }, [presets]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('timeFormat', timeFormat);
    }
  }, [timeFormat]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('meetingDuration', meetingDuration.toString());
    }
  }, [meetingDuration]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('showBestTimes', JSON.stringify(showBestTimes));
    }
  }, [showBestTimes]);

  useEffect(() => {
    if (typeof window !== 'undefined' && i18n.language) {
      localStorage.setItem('language', i18n.language);
    }
  }, [i18n.language]);

  const colorPalette = [
    '#2563eb', '#dc2626', '#059669', '#d97706', '#7c3aed', 
    '#db2777', '#0891b2', '#ea580c', '#4f46e5', '#be123c'
  ];

  useEffect(() => {
    // Debounce search to improve performance
    const debounceTimer = setTimeout(() => {
      if (searchQuery.trim() === '') {
        setFilteredCities([]);
        setSelectedIndex(0);
        return;
      }

      const query = searchQuery.toLowerCase().trim();

    // Timezone abbreviation map
    const timezoneAbbrevMap = {
      'pst': 'America/Los_Angeles',
      'pdt': 'America/Los_Angeles',
      'mst': 'America/Denver',
      'mdt': 'America/Denver',
      'cst': 'America/Chicago',
      'cdt': 'America/Chicago',
      'est': 'America/New_York',
      'edt': 'America/New_York',
      'gmt': 'Europe/London',
      'utc': 'UTC',
      'bst': 'Europe/London',
      'cet': 'Europe/Paris',
      'cest': 'Europe/Paris',
      'ist': 'Asia/Kolkata',
      'jst': 'Asia/Tokyo',
      'kst': 'Asia/Seoul',
      'aest': 'Australia/Sydney',
      'aedt': 'Australia/Sydney',
      'awst': 'Australia/Perth',
      'nzst': 'Pacific/Auckland',
      'nzdt': 'Pacific/Auckland'
    };

    // Country to cities map (sample - expand this)
    const countryToCities = {
      'australia': ['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide', 'Canberra'],
      'usa': ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia'],
      'united states': ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix'],
      'uk': ['London', 'Manchester', 'Birmingham', 'Glasgow', 'Liverpool'],
      'united kingdom': ['London', 'Manchester', 'Birmingham', 'Glasgow'],
      'canada': ['Toronto', 'Vancouver', 'Montreal', 'Calgary', 'Ottawa'],
      'japan': ['Tokyo', 'Osaka', 'Kyoto', 'Yokohama', 'Nagoya'],
      'india': ['Mumbai', 'Delhi', 'Bangalore', 'Kolkata', 'Chennai'],
      'china': ['Shanghai', 'Beijing', 'Hong Kong', 'Guangzhou', 'Shenzhen'],
      'germany': ['Berlin', 'Munich', 'Hamburg', 'Frankfurt', 'Cologne'],
      'france': ['Paris', 'Marseille', 'Lyon', 'Toulouse', 'Nice'],
      'spain': ['Madrid', 'Barcelona', 'Valencia', 'Seville', 'Bilbao'],
      'italy': ['Rome', 'Milan', 'Naples', 'Turin', 'Florence']
    };

    let matches = [];

    // Check timezone abbreviation first
    if (timezoneAbbrevMap[query]) {
      const tz = timezoneAbbrevMap[query];
      const cityForTz = Object.keys(cityTimezoneMap).find(city =>
        cityTimezoneMap[city] === tz
      );
      if (cityForTz) {
        matches.push({
          name: `${cityForTz} (${query.toUpperCase()})`,
          timezone: tz,
          matchType: 'timezone'
        });
      }
    }

    // Check country match
    const countryMatches = [];
    for (const [country, cities] of Object.entries(countryToCities)) {
      if (country.includes(query)) {
        cities.forEach(city => {
          if (cityTimezoneMap[city]) {
            countryMatches.push({
              name: `${city}, ${country.charAt(0).toUpperCase() + country.slice(1)}`,
              timezone: cityTimezoneMap[city],
              matchType: 'country'
            });
          }
        });
      }
    }

    // City name matches
    const cityMatches = Object.keys(cityTimezoneMap)
      .filter(city => city.toLowerCase().includes(query))
      .map(city => ({
        name: city,
        timezone: cityTimezoneMap[city],
        matchType: 'city'
      }));

    // Combine: timezone abbrev first, then exact city matches, then country matches, then partial matches
    const exactCityMatches = cityMatches.filter(m => m.name.toLowerCase().startsWith(query));
    const partialCityMatches = cityMatches.filter(m => !m.name.toLowerCase().startsWith(query));

    matches = [
      ...matches,
      ...exactCityMatches.slice(0, 5),
      ...countryMatches.slice(0, 3),
      ...partialCityMatches.slice(0, 3)
    ].slice(0, 10);

      setFilteredCities(matches);
      setSelectedIndex(0);
    }, 250); // 250ms debounce delay

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Load state from URL if present
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);

      // Support new compact format (?s=) and legacy format (?state=)
      const compactParam = params.get('s');
      const legacyParam = params.get('state');

      if (compactParam) {
        try {
          const compactData = JSON.parse(atob(compactParam));
          const reconstructed = compactData.map((item, index) => {
            // Find city name by reverse lookup in cityTimezoneMap
            const cityName = Object.keys(cityTimezoneMap).find(city => cityTimezoneMap[city] === item.tz);
            const displayName = cityName ? cityName.split(' (')[0].split(', ')[0] : item.tz.split('/')[1].replace(/_/g, ' ');

            return {
              id: Date.now() + index,
              name: displayName,
              timezone: item.tz,
              color: colorPalette[index % colorPalette.length],
              isPrimary: item.p === 1 || index === 0,
              businessHourStart: item.s ?? 9,
              businessHourEnd: item.e ?? 17
            };
          });
          setLocations(reconstructed);
        } catch (error) {
          console.error('Failed to load compact state from URL:', error);
        }
      } else if (legacyParam) {
        try {
          const decoded = JSON.parse(atob(legacyParam));
          setLocations(decoded);
        } catch (error) {
          console.error('Failed to load state from URL:', error);
        }
      }
    }
  }, []);

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => setShowToast(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  // Detect mobile viewport
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-detect user's timezone on first load
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const hasAutoDetected = localStorage.getItem('hasAutoDetectedTimezone');
    if (!hasAutoDetected && locations.length === 2) {
      // Only auto-detect if we still have the default locations
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const defaultTimezones = locations.map(loc => loc.timezone);

      if (!defaultTimezones.includes(userTimezone)) {
        // Find city name for user's timezone
        const cityName = Object.keys(cityTimezoneMap).find(
          city => cityTimezoneMap[city] === userTimezone
        );

        if (cityName) {
          addLocationFromCity(cityName, userTimezone);
          setPrimaryLocation(Date.now());
          setToastMessage(`Auto-detected your timezone: ${cityName}`);
          setShowToast(true);
        }

        localStorage.setItem('hasAutoDetectedTimezone', 'true');
      }
    }
  }, []);

  // Auto-hide swipe-to-delete trash button after 20 seconds
  useEffect(() => {
    if (!swipedLocation) return;

    const timer = setTimeout(() => {
      // Find the location card element and reset its transform
      const locationCard = document.querySelector(`[data-location-id="${swipedLocation}"]`);
      if (locationCard) {
        locationCard.style.transition = 'transform 0.3s ease';
        locationCard.style.transform = 'translateX(0)';
        setTimeout(() => {
          locationCard.style.transition = '';
        }, 300);
      }
      setSwipedLocation(null);
    }, 20000); // 20 seconds

    return () => clearTimeout(timer);
  }, [swipedLocation]);

  // Global keyboard shortcuts
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleGlobalKeyboard = (e) => {
      // Don't trigger shortcuts when typing in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;

      switch(e.key) {
        case '?':
        case '/':
          e.preventDefault();
          setShowHelp(true);
          break;
        case 'n':
        case 'N':
          e.preventDefault();
          setShowAdd(true);
          break;
        case 't':
        case 'T':
          e.preventDefault();
          setTheme(theme === 'dark' ? 'light' : 'dark');
          break;
        case 'Escape':
          setShowHelp(false);
          setShowAdd(false);
          setSearchQuery('');
          setEditingHours(null);
          break;
      }
    };

    window.addEventListener('keydown', handleGlobalKeyboard);
    return () => window.removeEventListener('keydown', handleGlobalKeyboard);
  }, [theme, showAdd]);

  // Set up passive touch event listeners to avoid blocking scrolling and button clicks
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let pullStartY = 0;
    let currentPullDistance = 0;
    let pulling = false;

    const touchStart = (e) => {
      // Don't interfere with interactive elements
      const target = e.target;
      if (target.tagName === 'BUTTON' || target.tagName === 'A' || target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.closest('button') || target.closest('a')) {
        return;
      }
      if (!isMobile || window.scrollY > 0) return;

      pullStartY = e.touches[0].clientY;
      pulling = true;
      setIsPulling(true);
      setPullDistance(0);
    };

    const touchMove = (e) => {
      if (!pulling || window.scrollY > 0) return;
      const distance = Math.max(0, Math.min(e.touches[0].clientY - pullStartY, 100));
      currentPullDistance = distance;
      setPullDistance(distance);
    };

    const touchEnd = () => {
      if (currentPullDistance > 60) {
        hapticFeedback('success');
        setToastMessage('Refreshed!');
        setShowToast(true);
        setCurrentTime(new Date());
      }
      pulling = false;
      currentPullDistance = 0;
      setIsPulling(false);
      setPullDistance(0);
    };

    // Add with passive: false only for touchstart to allow preventDefault if needed
    window.addEventListener('touchstart', touchStart, { passive: true });
    window.addEventListener('touchmove', touchMove, { passive: true });
    window.addEventListener('touchend', touchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', touchStart);
      window.removeEventListener('touchmove', touchMove);
      window.removeEventListener('touchend', touchEnd);
    };
  }, [isMobile]);

  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Detect user's current timezone and suggest a location
  const userLocationSuggestion = useMemo(() => {
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Find a city in the user's timezone
      const cityName = Object.keys(cityTimezoneMap).find(
        city => cityTimezoneMap[city] === userTimezone
      );
      if (cityName && userTimezone) {
        return { city: cityName, timezone: userTimezone };
      }
    } catch (error) {
      console.error('Failed to detect user timezone:', error);
    }
    return null;
  }, []);

  // Haptic feedback
  const hapticFeedback = (style = 'medium') => {
    if (typeof window !== 'undefined' && 'vibrate' in navigator) {
      const patterns = {
        light: 10,
        medium: 20,
        heavy: 30,
        success: [10, 50, 10],
        error: [20, 100, 20]
      };
      navigator.vibrate(patterns[style] || patterns.medium);
    }
  };

  // Toggle favorite
  const toggleFavorite = useCallback((timezone) => {
    hapticFeedback('light');
    setFavorites(prev => {
      if (prev.includes(timezone)) {
        return prev.filter(tz => tz !== timezone);
      } else {
        return [...prev, timezone];
      }
    });
  }, []);

  const addLocationFromCity = useCallback((cityName, timezone) => {
    // Extract just the city name if it has additional info
    const cleanName = cityName.split(' (')[0].split(', ')[0];

    // Check for duplicate timezone
    if (locations.some(loc => loc.timezone === timezone)) {
      hapticFeedback('error');
      setToastMessage(`${cleanName} is already added!`);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }

    const usedColors = locations.map(l => l.color);
    const availableColors = colorPalette.filter(c => !usedColors.includes(c));
    const color = availableColors.length > 0 ? availableColors[0] : colorPalette[locations.length % colorPalette.length];

    // If there's no primary location, make this new location primary
    const hasPrimary = locations.some(loc => loc.isPrimary);
    const newLocation = {
      id: Date.now(),
      name: cleanName,
      timezone: timezone,
      color,
      isPrimary: !hasPrimary,
      businessHourStart: 9,
      businessHourEnd: 17
    };

    // If this is the primary location, add it first; otherwise add it at the end
    setLocations(!hasPrimary ? [newLocation, ...locations] : [...locations, newLocation]);
    setSearchQuery('');
    setShowAdd(false);
    hapticFeedback('success');
  }, [locations]);

  const setPrimaryLocation = useCallback((id) => {
    const updatedLocations = locations.map(loc => ({
      ...loc,
      isPrimary: loc.id === id
    }));

    // Reorder so primary is first
    const primaryLoc = updatedLocations.find(loc => loc.isPrimary);
    const otherLocs = updatedLocations.filter(loc => !loc.isPrimary);

    setLocations(primaryLoc ? [primaryLoc, ...otherLocs] : updatedLocations);
    hapticFeedback('medium');
  }, [locations]);

  const handleKeyDown = useCallback((e) => {
    if (filteredCities.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % filteredCities.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filteredCities.length) % filteredCities.length);
    } else if (e.key === 'Enter' && filteredCities.length > 0) {
      e.preventDefault();
      const selected = filteredCities[selectedIndex];
      addLocationFromCity(selected.name, selected.timezone);
    } else if (e.key === 'Escape') {
      setSearchQuery('');
      setShowAdd(false);
    }
  }, [filteredCities, selectedIndex, addLocationFromCity]);

  const removeLocation = useCallback((id) => {
    const locationToRemove = locations.find(l => l.id === id);
    const remainingLocations = locations.filter(l => l.id !== id);

    // If we're removing the primary location and there are other locations,
    // make the first remaining location primary
    if (locationToRemove?.isPrimary && remainingLocations.length > 0) {
      remainingLocations[0].isPrimary = true;
    }

    // Save to undo stack
    const undoAction = {
      type: 'remove',
      location: locationToRemove,
      previousLocations: [...locations],
      timestamp: Date.now()
    };
    setUndoStack([undoAction, ...undoStack.slice(0, 2)]); // Keep only last 3 actions

    setLocations(remainingLocations);
    hapticFeedback('medium');
    setSwipedLocation(null);
    setSwipeX(0);
    setToastMessage(`${locationToRemove.name} removed`);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  }, [locations, undoStack]);

  const undoRemove = useCallback(() => {
    if (undoStack.length === 0) return;
    const lastAction = undoStack[0];
    if (lastAction.type === 'remove') {
      setLocations(lastAction.previousLocations);
      setUndoStack(undoStack.slice(1));
      setToastMessage(`${lastAction.location.name} restored`);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      hapticFeedback('success');
    }
  }, [undoStack]);

  // Swipe to delete handlers
  const handleLocationTouchStart = (e, locationId) => {
    if (!isMobile) return;
    const touch = e.touches[0];
    setSwipedLocation(locationId);
    setSwipeX(touch.clientX);

    // Long-press detection
    const longPressTimer = setTimeout(() => {
      hapticFeedback('heavy');
      setContextMenu({
        locationId,
        x: touch.clientX,
        y: touch.clientY
      });
      setSwipedLocation(null);
    }, 500);

    e.currentTarget.longPressTimer = longPressTimer;
  };

  const handleLocationTouchMove = (e, locationId) => {
    if (!isMobile || swipedLocation !== locationId) return;

    // Cancel long-press if moved
    if (e.currentTarget.longPressTimer) {
      clearTimeout(e.currentTarget.longPressTimer);
      e.currentTarget.longPressTimer = null;
    }

    const touch = e.touches[0];
    const deltaX = touch.clientX - swipeX;

    // Get current transform value
    const currentTransform = parseInt(e.currentTarget.style.transform.replace(/[^-\d]/g, '') || '0');

    // If already swiped left (delete button showing), allow swiping right to close
    if (currentTransform < -60 && deltaX > 0) {
      // Allow swipe right to close
      const newTransform = Math.max(Math.min(currentTransform + deltaX, 0), -100);
      e.preventDefault();
      e.currentTarget.style.transform = `translateX(${newTransform}px)`;
      setSwipeX(touch.clientX); // Update swipeX for continuous tracking
    } else if (deltaX < 0) {
      // Normal left swipe to reveal delete button
      e.preventDefault();
      e.currentTarget.style.transform = `translateX(${Math.max(deltaX, -100)}px)`;
    }
  };

  const handleLocationTouchEnd = (e, locationId) => {
    // Cancel long-press timer
    if (e.currentTarget.longPressTimer) {
      clearTimeout(e.currentTarget.longPressTimer);
      e.currentTarget.longPressTimer = null;
    }

    if (!isMobile || swipedLocation !== locationId) return;
    const deltaX = parseInt(e.currentTarget.style.transform.replace(/[^-\d]/g, '') || '0');

    if (deltaX < -60) {
      // Swiped far enough - show delete button
      e.currentTarget.style.transform = 'translateX(-80px)';
      hapticFeedback('medium');
    } else if (deltaX > -30) {
      // Swiped back or not far enough - reset
      e.currentTarget.style.transform = 'translateX(0)';
      setSwipedLocation(null);
    }
  };

  const getPrimaryLocation = useMemo(() => {
    return locations.find(loc => loc.isPrimary);
  }, [locations]);

  const getTimeForLocation = useCallback((timezone) => {
    return new Date(currentTime.toLocaleString('en-US', { timeZone: timezone }));
  }, [currentTime]);

  const getCurrentHourForLocation = (timezone) => {
    return getTimeForLocation(timezone).getHours();
  };

  const isBusinessHour = (hour, location, timezone) => {
    const start = location?.businessHourStart ?? 9;
    const end = location?.businessHourEnd ?? 17;

    // Check if hour is within business hours range
    if (hour < start || hour >= end) return false;

    // Get the UTC timestamp for this hour in this timezone using our reliable function
    const tz = timezone || location?.timezone;
    const now = new Date();

    // Get today's date in the target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const parts = formatter.formatToParts(now);
    const targetYear = parseInt(parts.find(p => p.type === 'year').value);
    const targetMonth = parseInt(parts.find(p => p.type === 'month').value);
    const targetDay = parseInt(parts.find(p => p.type === 'day').value);

    // Use the same algorithm as getTimeForHourInTimezone to find the correct UTC time
    let utcDate = new Date(Date.UTC(targetYear, targetMonth - 1, targetDay, hour, 0, 0));

    const verifyFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: '2-digit',
      hour12: false,
      day: '2-digit'
    });

    let verifyParts = verifyFormatter.formatToParts(utcDate);
    let displayedHour = parseInt(verifyParts.find(p => p.type === 'hour').value);
    let displayedDay = parseInt(verifyParts.find(p => p.type === 'day').value);

    // Adjust until both hour and day are correct
    let attempts = 0;
    while ((displayedHour !== hour || displayedDay !== targetDay) && attempts < 48) {
      let hourDiff = hour - displayedHour;
      if (displayedDay !== targetDay) {
        const dayDiff = targetDay - displayedDay;
        hourDiff += dayDiff * 24;
      }
      utcDate = new Date(utcDate.getTime() + hourDiff * 60 * 60 * 1000);
      verifyParts = verifyFormatter.formatToParts(utcDate);
      displayedHour = parseInt(verifyParts.find(p => p.type === 'hour').value);
      displayedDay = parseInt(verifyParts.find(p => p.type === 'day').value);
      attempts++;
    }

    return true;
  };

  const updateBusinessHours = useCallback((locationId, start, end) => {
    setLocations(prevLocations =>
      prevLocations.map(loc =>
        loc.id === locationId ? { ...loc, businessHourStart: start, businessHourEnd: end } : loc
      )
    );
    setEditingHours(null);
  }, []);

  const shareURL = async () => {
    // Compact encoding: only store essential data
    const compactData = locations.map((loc, idx) => ({
      tz: loc.timezone,
      ...(loc.businessHourStart !== 9 || loc.businessHourEnd !== 17 ? {
        s: loc.businessHourStart,
        e: loc.businessHourEnd
      } : {}),
      ...(loc.isPrimary ? { p: 1 } : {})
    }));
    const state = btoa(JSON.stringify(compactData));
    const url = `${window.location.origin}${window.location.pathname}?s=${state}`;

    // Try Web Share API first (native mobile sharing)
    if (navigator.share && isMobile) {
      try {
        await navigator.share({
          title: 'Clockbridge - Timezone Comparison',
          text: `Compare times across ${locations.length} locations`,
          url: url
        });
        hapticFeedback('success');
        setToastMessage('Shared successfully!');
        setShowToast(true);
        return;
      } catch (err) {
        // User cancelled or share failed, fall back to clipboard
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    }

    // Fallback to clipboard
    try {
      await navigator.clipboard.writeText(url);
      hapticFeedback('success');
      setToastMessage('Share URL copied to clipboard!');
      setShowToast(true);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      hapticFeedback('error');
      setToastMessage('Failed to copy URL');
      setShowToast(true);
    }
  };

  const generateICSFile = (slot, slotIndex) => {
    const primary = getPrimaryLocation;
    const startHour = slot.primaryHour;

    // Create start and end dates for the meeting
    const now = new Date();
    // Find next weekday (skip weekends)
    let daysToAdd = 1;
    const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysToAdd, startHour, 0, 0);
    while (startDate.getDay() === 0 || startDate.getDay() === 6) {
      daysToAdd++;
      startDate.setDate(now.getDate() + daysToAdd);
    }
    const endDate = new Date(startDate.getTime() + meetingDuration * 60 * 1000);

    // Format date for ICS (YYYYMMDDTHHmmss)
    const formatICSDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}${month}${day}T${hours}${minutes}${seconds}`;
    };

    const title = `Team Meeting - ${locations.map(l => l.name).join(', ')}`;
    const description = `Optimal meeting time across all timezones:\\n\\n${slot.details.map(d => `${d.location}: ${d.hour.toString().padStart(2, '0')}:00`).join('\\n')}`;
    const location = 'Virtual';
    const timestamp = formatICSDate(new Date());

    // Generate unique UID for the event
    const uid = `clockbridge-${Date.now()}-${slotIndex}@clockbridge.app`;

    // Create ICS file content
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Clockbridge//Timezone Visualizer//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${timestamp}`,
      `DTSTART:${formatICSDate(startDate)}`,
      `DTEND:${formatICSDate(endDate)}`,
      `SUMMARY:${title}`,
      `DESCRIPTION:${description}`,
      `LOCATION:${location}`,
      'STATUS:CONFIRMED',
      'SEQUENCE:0',
      'BEGIN:VALARM',
      'TRIGGER:-PT15M',
      'ACTION:DISPLAY',
      'DESCRIPTION:Reminder',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    // Create blob and download
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `team-meeting-slot-${slotIndex + 1}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    hapticFeedback('success');
    setToastMessage('Calendar event downloaded!');
    setShowToast(true);
  };

  const getTimeForHourInTimezone = (timezone, hour, referenceDate = selectedDate) => {
    // Find the UTC timestamp that displays as "hour" on the reference date in the target timezone
    const now = referenceDate || new Date();

    // Get the date components in the target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const parts = formatter.formatToParts(now);
    const targetYear = parseInt(parts.find(p => p.type === 'year').value);
    const targetMonth = parseInt(parts.find(p => p.type === 'month').value);
    const targetDay = parseInt(parts.find(p => p.type === 'day').value);

    // Start with a guess: create a UTC date for the reference date at the desired hour
    // We'll adjust this until it formats correctly in the target timezone
    let utcDate = new Date(Date.UTC(targetYear, targetMonth - 1, targetDay, hour, 0, 0));

    // Check both hour and day that this UTC date shows in the target timezone
    const verifyFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      hour12: false,
      day: '2-digit'
    });

    let verifyParts = verifyFormatter.formatToParts(utcDate);
    let displayedHour = parseInt(verifyParts.find(p => p.type === 'hour').value);
    let displayedDay = parseInt(verifyParts.find(p => p.type === 'day').value);

    // Adjust the UTC time until BOTH hour and day are correct
    let attempts = 0;
    while ((displayedHour !== hour || displayedDay !== targetDay) && attempts < 48) {
      // Calculate adjustment needed
      let hourDiff = hour - displayedHour;

      // If the day is wrong, we need a bigger adjustment
      if (displayedDay !== targetDay) {
        const dayDiff = targetDay - displayedDay;
        hourDiff += dayDiff * 24;
      }

      utcDate = new Date(utcDate.getTime() + hourDiff * 60 * 60 * 1000);

      verifyParts = verifyFormatter.formatToParts(utcDate);
      displayedHour = parseInt(verifyParts.find(p => p.type === 'hour').value);
      displayedDay = parseInt(verifyParts.find(p => p.type === 'day').value);
      attempts++;
    }

    // Now format this UTC date in the target timezone
    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    const dayFormatter = new Intl.DateTimeFormat(i18n.language || 'en-US', {
      timeZone: timezone,
      weekday: 'short'
    });

    return {
      time: timeFormatter.format(utcDate),
      day: dayFormatter.format(utcDate),
      fullDate: utcDate
    };
  };

  // Fetch holidays for a specific country and year from Nager.Date API
  const fetchHolidaysForCountry = async (countryCode, year) => {
    try {
      const response = await fetch(
        `https://date.nager.at/api/v3/PublicHolidays/${year}/${countryCode}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Failed to fetch holidays for ${countryCode}:`, error);
      return [];
    }
  };

  // Get country code from timezone
  const getCountryCodeFromTimezone = (timezone) => {
    return timezoneToCountryCode[timezone] || null;
  };

  // Check if a specific date is a holiday in a given country
  const isDateHoliday = (dateStr, countryCode) => {
    if (!holidays[countryCode]) return null;
    return holidays[countryCode].find(h => h.date === dateStr) || null;
  };

  // Get holiday info for a specific hour in a timezone
  const getHolidayForHour = (hour, timezone) => {
    const countryCode = getCountryCodeFromTimezone(timezone);
    if (!countryCode) return null;

    const timeAndDay = getTimeForHourInTimezone(timezone, hour);
    const date = timeAndDay.fullDate;
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    return isDateHoliday(dateStr, countryCode);
  };

  const isWeekend = (hour, timezone) => {
    // Use the same approach as getTimeForHourInTimezone to ensure consistency
    const timeAndDay = getTimeForHourInTimezone(timezone, hour);

    // Check if the day is Saturday or Sunday
    const dayStr = timeAndDay.day;
    return dayStr === 'Sat' || dayStr === 'Sun';
  };

  const getCorrespondingTimeInTimezone = (primaryTimezone, targetTimezone, primaryHour, referenceDate = selectedDate) => {
    // First, get the UTC timestamp for primaryHour on the reference date in primaryTimezone
    const now = referenceDate || new Date();

    // Get the date in primary timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: primaryTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const parts = formatter.formatToParts(now);
    const targetYear = parseInt(parts.find(p => p.type === 'year').value);
    const targetMonth = parseInt(parts.find(p => p.type === 'month').value);
    const targetDay = parseInt(parts.find(p => p.type === 'day').value);

    // Find the UTC time that corresponds to primaryHour on the reference date in primaryTimezone
    let utcDate = new Date(Date.UTC(targetYear, targetMonth - 1, targetDay, primaryHour, 0, 0));

    const verifyFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: primaryTimezone,
      hour: '2-digit',
      hour12: false,
      day: '2-digit'
    });

    let verifyParts = verifyFormatter.formatToParts(utcDate);
    let displayedHour = parseInt(verifyParts.find(p => p.type === 'hour').value);
    let displayedDay = parseInt(verifyParts.find(p => p.type === 'day').value);

    // Adjust until BOTH hour and day are correct in the primary timezone
    let attempts = 0;
    while ((displayedHour !== primaryHour || displayedDay !== targetDay) && attempts < 48) {
      // Calculate adjustment needed
      let hourDiff = primaryHour - displayedHour;

      // If the day is wrong, we need a bigger adjustment
      if (displayedDay !== targetDay) {
        const dayDiff = targetDay - displayedDay;
        hourDiff += dayDiff * 24;
      }

      utcDate = new Date(utcDate.getTime() + hourDiff * 60 * 60 * 1000);

      verifyParts = verifyFormatter.formatToParts(utcDate);
      displayedHour = parseInt(verifyParts.find(p => p.type === 'hour').value);
      displayedDay = parseInt(verifyParts.find(p => p.type === 'day').value);
      attempts++;
    }

    // Now format this same UTC time in the target timezone
    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: targetTimezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    const dayFormatter = new Intl.DateTimeFormat(i18n.language || 'en-US', {
      timeZone: targetTimezone,
      weekday: 'short'
    });

    return {
      time: timeFormatter.format(utcDate),
      day: dayFormatter.format(utcDate),
      fullDate: utcDate
    };
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: timeFormat === '12'
    });
  };

  const getUTCOffset = (timezone) => {
    const date = new Date();
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    const offset = (tzDate - utcDate) / (1000 * 60 * 60);
    const sign = offset >= 0 ? '+' : '';
    return `UTC${sign}${offset}`;
  };

  const getTimezoneDetails = (timezone) => {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: timeFormat === '12',
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const localTime = formatter.format(date);

    // Get major cities for this timezone
    const citiesInTimezone = Object.keys(cityTimezoneMap)
      .filter(city => cityTimezoneMap[city] === timezone)
      .slice(0, 3);

    return {
      timezone,
      localTime,
      cities: citiesInTimezone,
      offset: getUTCOffset(timezone)
    };
  };

  // Score how reasonable a time is for someone to attend a meeting
  // Now considers custom business hours, meeting duration, and uses smooth decay
  const scoreTimeReasonability = (hour, minute = 0, location = null, meetingDurationHours = 1) => {
    // hour is in 24-hour format (0-23), minute is 0-59
    const timeInHours = hour + minute / 60;
    const meetingEndTime = timeInHours + meetingDurationHours;

    // Get custom business hours if location provided, otherwise use defaults
    const businessStart = location?.businessHourStart ?? 9;
    const businessEnd = location?.businessHourEnd ?? 17;

    // Check if meeting extends beyond business hours
    const startsInBusiness = timeInHours >= businessStart && timeInHours < businessEnd;
    const endsInBusiness = meetingEndTime <= businessEnd;

    // Perfect: Fully within business hours
    if (startsInBusiness && endsInBusiness) {
      // Slightly prefer middle of business hours (avoid start/end of day)
      const businessMid = (businessStart + businessEnd) / 2;
      const distFromMid = Math.abs(timeInHours - businessMid);
      const maxDist = (businessEnd - businessStart) / 2;
      return 100 - (distFromMid / maxDist) * 10; // 90-100 range
    }

    // Good: Starts in business hours but runs slightly over
    if (startsInBusiness && !endsInBusiness && meetingEndTime < businessEnd + 2) {
      return 75 - Math.min((meetingEndTime - businessEnd) * 10, 15); // 60-75 range
    }

    // Acceptable: Close to business hours
    const distFromStart = Math.abs(timeInHours - businessStart);
    const distFromEnd = Math.abs(timeInHours - businessEnd);
    const minDistFromBusiness = Math.min(distFromStart, distFromEnd);

    if (minDistFromBusiness <= 2) {
      // Within 2 hours of business hours - exponential decay
      return Math.max(30, 60 - minDistFromBusiness * 15); // 30-60 range
    }

    // Poor: Early morning or late evening
    if (hour >= 6 && hour < businessStart - 2) {
      const hoursBeforeStart = businessStart - 2 - timeInHours;
      return Math.max(10, 30 - hoursBeforeStart * 5); // 10-30 range
    }

    if (hour >= businessEnd + 2 && hour < 23) {
      const hoursAfterEnd = timeInHours - (businessEnd + 2);
      return Math.max(10, 30 - hoursAfterEnd * 5); // 10-30 range
    }

    // Very poor: Night hours (11pm-6am)
    if (hour >= 23 || hour < 6) {
      // Worst in middle of night (2-4am)
      if (hour >= 2 && hour < 4) return 0;
      return 5; // Slightly better than dead of night
    }

    return 10; // Default for edge cases
  };

  const findBestMeetingTimes = useCallback(() => {
    if (locations.length === 0) {
      setBestMeetingTimes([]);
      setShowBestTimes(false);
      return;
    }

    const primary = getPrimaryLocation;
    if (!primary) {
      setBestMeetingTimes([]);
      setShowBestTimes(false);
      return;
    }

    // Convert meeting duration from minutes to hours
    const durationInHours = meetingDuration / 60;

    // For each half-hour slot in the primary timezone, calculate a "reasonability score"
    const scoredTimes = [];

    // Check both on-the-hour and half-hour slots for better granularity
    for (let primaryHour = 0; primaryHour < 24; primaryHour++) {
      for (let minute of [0, 30]) {
        let totalScore = 0;
        let worstScore = 100;
        const timeDetails = [];
        const unreasonableLocations = []; // Locations with score < 30
        let isWeekendForAny = false;

        for (const location of locations) {
          let correspondingHour = primaryHour;
          let correspondingMinute = minute;

          if (!location.isPrimary) {
            // Calculate corresponding time in this timezone
            const correspondingTime = getCorrespondingTimeInTimezone(primary.timezone, location.timezone, primaryHour);
            const timeMatch = correspondingTime.time.match(/(\d+):(\d+)\s*(AM|PM)/i);

            if (timeMatch) {
              correspondingHour = parseInt(timeMatch[1]);
              correspondingMinute = parseInt(timeMatch[2]);
              if (timeMatch[3].toUpperCase() === 'PM' && correspondingHour !== 12) correspondingHour += 12;
              else if (timeMatch[3].toUpperCase() === 'AM' && correspondingHour === 12) correspondingHour = 0;
            }

            // Adjust for the :30 offset if needed
            if (minute === 30) {
              correspondingMinute += 30;
              if (correspondingMinute >= 60) {
                correspondingMinute -= 60;
                correspondingHour = (correspondingHour + 1) % 24;
              }
            }
          }

          // Check if it's a weekend for this location
          const isWeekendHour = isWeekend(correspondingHour, location.timezone);
          if (isWeekendHour) isWeekendForAny = true;

          // Score this time for this location with custom business hours and duration
          const score = scoreTimeReasonability(correspondingHour, correspondingMinute, location, durationInHours);

          // Apply weekend penalty if it's a weekend
          const adjustedScore = isWeekendHour ? score * 0.5 : score;

          totalScore += adjustedScore;
          worstScore = Math.min(worstScore, adjustedScore);

          const isBusiness = isBusinessHour(correspondingHour, location, location.timezone);

          if (adjustedScore < 30) {
            unreasonableLocations.push({
              name: location.name,
              hour: correspondingHour,
              minute: correspondingMinute,
              score: adjustedScore,
              isWeekend: isWeekendHour
            });
          }

          timeDetails.push({
            location: location.name,
            hour: correspondingHour,
            minute: correspondingMinute,
            timezone: location.timezone,
            score: adjustedScore,
            inBusinessHours: isBusiness,
            isWeekend: isWeekendHour
          });
        }

        // Calculate average score
        const avgScore = totalScore / locations.length;

        // Calculate how many are in business hours
        const inBusinessCount = timeDetails.filter(t => t.inBusinessHours).length;

        scoredTimes.push({
          primaryHour,
          primaryMinute: minute,
          totalScore,
          avgScore,
          worstScore,          // The worst score among all locations (minimize suffering)
          details: timeDetails,
          inBusinessCount,
          totalCount: locations.length,
          unreasonableLocations,
          isWeekendForAny,
          isPerfect: inBusinessCount === locations.length && worstScore >= 90 && !isWeekendForAny
        });
      }
    }

    // Improved sorting algorithm:
    // 1. Prioritize times where majority are in business hours
    // 2. Then by average score (overall happiness)
    // 3. Then by worst score (minimize suffering)
    // 4. Avoid dead-of-night (2-4am) times unless absolutely necessary
    scoredTimes.sort((a, b) => {
      // Check if any location is in dead-of-night (score 0)
      const aHasDeadNight = a.details.some(d => d.score === 0);
      const bHasDeadNight = b.details.some(d => d.score === 0);

      // Heavily penalize dead-of-night times
      if (aHasDeadNight && !bHasDeadNight) return 1;
      if (!aHasDeadNight && bHasDeadNight) return -1;

      // First, prioritize by number of locations in business hours (majority wins)
      if (b.inBusinessCount !== a.inBusinessCount) {
        return b.inBusinessCount - a.inBusinessCount;
      }

      // Then by average score (overall happiness)
      if (Math.abs(b.avgScore - a.avgScore) > 1) {
        return b.avgScore - a.avgScore;
      }

      // Then by worst score (minimize suffering)
      if (b.worstScore !== a.worstScore) {
        return b.worstScore - a.worstScore;
      }

      // Finally by total score
      return b.totalScore - a.totalScore;
    });

    // Intelligent filtering: Show best options even if not perfect
    let bestTimes;

    // Try to find times where majority (>50%) are in business hours
    const majorityInBusiness = scoredTimes.filter(t =>
      t.inBusinessCount > locations.length / 2 && !t.details.some(d => d.score === 0)
    );

    if (majorityInBusiness.length > 0) {
      // Great! We found times where most people are in business hours
      bestTimes = majorityInBusiness.slice(0, 5);
    } else {
      // No majority in business hours, find best compromise
      // Exclude only the truly terrible times (dead of night for anyone)
      const compromiseTimes = scoredTimes.filter(t =>
        !t.details.some(d => d.score === 0) && t.avgScore > 20
      );

      if (compromiseTimes.length > 0) {
        bestTimes = compromiseTimes.slice(0, 5);
      } else {
        // Even with large timezone spreads, show the least-bad options
        // Just avoid times where someone is in the 2-4am dead zone
        bestTimes = scoredTimes
          .filter(t => !t.details.some(d => d.score === 0))
          .slice(0, 5);
      }
    }

    // If still no results (very rare), show top 5 no matter what
    if (!bestTimes || bestTimes.length === 0) {
      bestTimes = scoredTimes.slice(0, 5);
    }

    setBestMeetingTimes(bestTimes);
    setShowBestTimes(true);
    hapticFeedback('success');

    if (bestTimes.length > 0) {
      const topTime = bestTimes[0];
      const hasUnreasonable = topTime.unreasonableLocations.length > 0;
      const majorityInBusiness = topTime.inBusinessCount > locations.length / 2;

      let message = `Found ${bestTimes.length} meeting ${bestTimes.length === 1 ? 'option' : 'options'}!`;

      if (topTime.isPerfect) {
        message = `Perfect! ${bestTimes.length} ${bestTimes.length === 1 ? 'slot' : 'slots'} where everyone is in business hours.`;
      } else if (majorityInBusiness && topTime.inBusinessCount === locations.length - 1) {
        message = `Great option! ${topTime.inBusinessCount} of ${locations.length} locations in business hours.`;
      } else if (majorityInBusiness) {
        message = `Good compromise: majority (${topTime.inBusinessCount}/${locations.length}) in business hours.`;
      } else if (topTime.isWeekendForAny) {
        message = `Best available (weekend for some). Large timezone spread detected.`;
      } else if (hasUnreasonable) {
        message = `Best compromise found. Large timezone spread makes perfect overlap difficult.`;
      }

      setToastMessage(message);
      setShowToast(true);
    } else {
      // This should rarely happen now with the improved algorithm
      setToastMessage('No meeting times found. Please check your locations.');
      setShowToast(true);
      setBestMeetingTimes([]);
      setShowBestTimes(false);
    }
  }, [locations, getPrimaryLocation, meetingDuration, selectedDate]);

  // Auto-recalculate best meeting times when locations or primary changes
  useEffect(() => {
    // Automatically recalculate when locations change (unless user has toggled off)
    if (showBestTimes && locations.length > 0) {
      findBestMeetingTimes();
    }
  }, [locations, showBestTimes, findBestMeetingTimes]);

  // Fetch holidays when locations or selected date change
  useEffect(() => {
    const fetchHolidays = async () => {
      const currentYear = selectedDate.getFullYear();
      const nextYear = currentYear + 1;
      const newHolidays = {};

      // Get unique country codes from all locations
      const countryCodes = [...new Set(
        locations
          .map(loc => getCountryCodeFromTimezone(loc.timezone))
          .filter(code => code !== null)
      )];

      // Fetch holidays for current and next year for each country
      for (const countryCode of countryCodes) {
        try {
          const [currentYearData, nextYearData] = await Promise.all([
            fetchHolidaysForCountry(countryCode, currentYear),
            fetchHolidaysForCountry(countryCode, nextYear)
          ]);
          newHolidays[countryCode] = [...currentYearData, ...nextYearData];
        } catch (error) {
          console.error(`Error fetching holidays for ${countryCode}:`, error);
        }
      }

      setHolidays(newHolidays);
    };

    if (locations.length > 0) {
      fetchHolidays();
    }
  }, [locations, selectedDate]);

  const savePreset = useCallback(() => {
    if (!presetName.trim()) {
      setToastMessage('Please enter a preset name');
      setShowToast(true);
      return;
    }

    if (locations.length === 0) {
      setToastMessage('Add some locations first');
      setShowToast(true);
      return;
    }

    const newPreset = {
      id: Date.now(),
      name: presetName.trim(),
      locations: locations.map(loc => ({
        name: loc.name,
        timezone: loc.timezone,
        isPrimary: loc.isPrimary,
        businessHourStart: loc.businessHourStart,
        businessHourEnd: loc.businessHourEnd
      })),
      createdAt: new Date().toISOString()
    };

    setPresets([...presets, newPreset]);
    setPresetName('');
    setShowPresetModal(false);
    hapticFeedback('success');
    setToastMessage(`Preset "${newPreset.name}" saved!`);
    setShowToast(true);
  }, [locations, presetName, presets]);

  const loadPreset = useCallback((preset) => {
    const loadedLocations = preset.locations.map((loc, index) => ({
      ...loc,
      id: Date.now() + index,
      color: colorPalette[index % colorPalette.length]
    }));

    setLocations(loadedLocations);
    hapticFeedback('success');
    setToastMessage(`Loaded preset "${preset.name}"`);
    setShowToast(true);
  }, []);

  const deletePreset = useCallback((presetId) => {
    setPresets(presets.filter(p => p.id !== presetId));
    hapticFeedback('medium');
    setToastMessage('Preset deleted');
    setShowToast(true);
  }, [presets]);

  const themeColors = useMemo(() => theme === 'dark' ? {
    bg: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
    text: 'white',
    textMuted: 'rgba(255, 255, 255, 0.6)',
    textDim: 'rgba(255, 255, 255, 0.3)',
    textBright: 'rgba(255, 255, 255, 0.9)',
    border: 'rgba(255, 255, 255, 0.1)',
    borderStrong: 'rgba(255, 255, 255, 0.2)',
    panelBg: 'rgba(255, 255, 255, 0.03)',
    panelBgStrong: 'rgba(15, 23, 42, 0.98)',
    inputBg: 'rgba(255, 255, 255, 0.05)',
    headerBg: 'rgba(0, 0, 0, 0.2)',
    locationBg: 'rgba(0, 0, 0, 0.1)',
    closeBtn: 'rgba(255, 255, 255, 0.3)'
  } : {
    bg: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)',
    text: '#0f172a',
    textMuted: '#475569',
    textDim: '#94a3b8',
    textBright: '#020617',
    border: 'rgba(0, 0, 0, 0.15)',
    borderStrong: 'rgba(0, 0, 0, 0.25)',
    panelBg: 'rgba(255, 255, 255, 0.8)',
    panelBgStrong: 'rgba(255, 255, 255, 0.95)',
    inputBg: 'rgba(255, 255, 255, 0.9)',
    headerBg: 'rgba(255, 255, 255, 0.5)',
    locationBg: 'rgba(0, 0, 0, 0.05)',
    closeBtn: 'rgba(0, 0, 0, 0.4)'
  }, [theme]);

  // Memoize the style tag to prevent recreation on every render
  const styles = useMemo(() => (
    <style>{`
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
        font-variant-numeric: tabular-nums;
      }

      html {
        height: 100%;
        background: ${themeColors.bg};
      }

      body {
        margin: 0;
        padding: 0;
        min-height: 100%;
        overflow-x: hidden;
        background: ${themeColors.bg};
      }

      #root {
        margin: 0;
        padding: 0;
        min-height: 100vh;
        background: ${themeColors.bg};
      }

      @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Sora:wght@300;400;600;700&display=swap');

      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(100%);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes scaleIn {
        from {
          opacity: 0;
          transform: scale(0.9);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }

      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }

      .location-row {
        animation: slideIn 0.4s ease-out;
      }

      .hour-cell {
        transition: all 0.2s ease;
      }

      .hour-cell:hover {
        transform: scale(1.05);
        z-index: 10;
      }

      input, select {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        color: white;
        padding: 0.75rem 1rem;
        border-radius: 0.5rem;
        font-family: 'Sora', sans-serif;
        font-size: 0.875rem;
        transition: all 0.2s ease;
        box-sizing: border-box;
        min-height: 44px;
        touch-action: manipulation;
      }

      input:focus, select:focus {
        outline: none;
        border-color: rgba(255, 255, 255, 0.3);
        background: rgba(255, 255, 255, 0.08);
      }

      input::placeholder {
        color: rgba(255, 255, 255, 0.3);
      }

      input[type="time"]::-webkit-calendar-picker-indicator {
        filter: ${theme === 'dark' ? 'invert(1)' : 'invert(0)'};
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: all 0.2s ease;
      }

      input[type="time"]::-webkit-calendar-picker-indicator:hover {
        background: ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
      }

      input[type="time"]:focus {
        border-color: #60a5fa !important;
        box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.1);
      }

      input[type="time"]::-webkit-datetime-edit-fields-wrapper {
        padding: 0;
      }

      input[type="time"]::-webkit-datetime-edit-hour-field,
      input[type="time"]::-webkit-datetime-edit-minute-field,
      input[type="time"]::-webkit-datetime-edit-ampm-field {
        padding: 2px 4px;
        border-radius: 3px;
        transition: all 0.2s ease;
      }

      input[type="time"]::-webkit-datetime-edit-hour-field:focus,
      input[type="time"]::-webkit-datetime-edit-minute-field:focus,
      input[type="time"]::-webkit-datetime-edit-ampm-field:focus {
        background: rgba(96, 165, 250, 0.2);
        color: #60a5fa;
      }

      input[type="date"]::-webkit-calendar-picker-indicator {
        filter: ${theme === 'dark' ? 'invert(1)' : 'invert(0)'};
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: all 0.2s ease;
      }

      input[type="date"]::-webkit-calendar-picker-indicator:hover {
        background: ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
      }

      input[type="date"]:focus {
        border-color: #60a5fa !important;
        box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.1);
      }

      .glass-panel {
        background: ${themeColors.panelBgStrong};
        backdrop-filter: blur(20px);
        -webkit-backdrop-filter: blur(20px);
        border: 1px solid ${themeColors.border};
      }

      .modal-backdrop {
        animation: fadeIn 0.2s ease-out;
      }

      .modal-content {
        animation: scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
      }

      button {
        transition: all 0.2s ease;
      }

      button:active {
        transform: scale(0.95);
      }

      /* Scrollbar styling for webkit browsers */
      ::-webkit-scrollbar {
        width: 14px;
        height: 14px;
      }

      ::-webkit-scrollbar-track {
        background: ${theme === 'dark' ? 'rgb(15, 23, 42)' : 'rgb(241, 245, 249)'};
      }

      ::-webkit-scrollbar-thumb {
        background: ${theme === 'dark' ? 'rgb(51, 65, 85)' : 'rgb(203, 213, 225)'};
        border-radius: 7px;
        border: 3px solid ${theme === 'dark' ? 'rgb(15, 23, 42)' : 'rgb(241, 245, 249)'};
      }

      ::-webkit-scrollbar-thumb:hover {
        background: ${theme === 'dark' ? 'rgb(71, 85, 105)' : 'rgb(148, 163, 184)'};
      }

      ::-webkit-scrollbar-corner {
        background: ${theme === 'dark' ? 'rgb(15, 23, 42)' : 'rgb(241, 245, 249)'};
      }

      /* Scrollbar styling for Firefox */
      * {
        scrollbar-width: thin;
        scrollbar-color: ${theme === 'dark' ? 'rgb(51, 65, 85) rgb(15, 23, 42)' : 'rgb(203, 213, 225) rgb(241, 245, 249)'};
      }

      @media (max-width: 768px) {
        input, select {
          font-size: 16px;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }
      }
    `}</style>
  ), [theme, themeColors.bg, themeColors.panelBgStrong, themeColors.border]);

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        background: themeColors.bg,
        padding: '0',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        margin: '0',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        boxSizing: 'border-box'
      }}
    >
      {styles}


      {/* Pull to refresh indicator */}
      {isPulling && pullDistance > 0 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: '50%',
          transform: `translateX(-50%) translateY(${Math.min(pullDistance, 60)}px)`,
          zIndex: 10002,
          transition: 'transform 0.2s ease'
        }}>
          <div style={{
            padding: '0.5rem 1rem',
            background: themeColors.panelBgStrong,
            borderRadius: '2rem',
            border: `1px solid ${themeColors.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: themeColors.text,
            fontSize: '0.875rem',
            fontFamily: "'Sora', sans-serif",
            opacity: pullDistance / 60
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              border: `2px solid ${themeColors.border}`,
              borderTopColor: '#60a5fa',
              animation: pullDistance > 60 ? 'spin 0.8s linear infinite' : 'none',
              transform: `rotate(${pullDistance * 3}deg)`
            }} />
            {pullDistance > 60 ? 'Release to refresh' : 'Pull to refresh'}
          </div>
        </div>
      )}

      <div style={{ maxWidth: '1800px', margin: '0 auto', padding: isMobile ? '1.5rem 1rem' : '3rem 2rem' }}>
        <header style={{
          marginBottom: isMobile ? '2rem' : '3rem',
          animation: 'fadeIn 0.6s ease-out'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.75rem' : '1rem' }}>
              {/* Clockbridge Logo */}
              <svg width={isMobile ? 36 : 48} height={isMobile ? 36 : 48} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Clock Circle */}
                <circle cx="24" cy="24" r="20" stroke="#60a5fa" strokeWidth="2.5" fill="none" opacity="0.8"/>
                {/* Clock Hands */}
                <line x1="24" y1="24" x2="24" y2="12" stroke="#60a5fa" strokeWidth="2.5" strokeLinecap="round"/>
                <line x1="24" y1="24" x2="32" y2="24" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" opacity="0.7"/>
                {/* Bridge Arc */}
                <path d="M 8 24 Q 24 8, 40 24" stroke="#60a5fa" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.6"/>
                {/* Bridge Pillars */}
                <line x1="12" y1="24" x2="12" y2="32" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
                <line x1="24" y1="16" x2="24" y2="32" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
                <line x1="36" y1="24" x2="36" y2="32" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
                {/* Center Dot */}
                <circle cx="24" cy="24" r="2" fill="#60a5fa"/>
              </svg>
              <h1 style={{
                fontSize: isMobile ? '1.75rem' : '3rem',
                fontWeight: '700',
                color: themeColors.text,
                margin: 0,
                fontFamily: "'Sora', sans-serif",
                letterSpacing: '-0.02em'
              }}>
                clockbridge
              </h1>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Date Picker */}
              <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                <input
                  type="date"
                  value={selectedDate.toISOString().split('T')[0]}
                  min={(() => {
                    const minDate = new Date();
                    minDate.setDate(minDate.getDate() - 2); // 48 hours (2 days) in the past
                    return minDate.toISOString().split('T')[0];
                  })()}
                  onChange={(e) => {
                    hapticFeedback('light');
                    const newDate = new Date(e.target.value + 'T12:00:00');
                    setSelectedDate(newDate);
                  }}
                  style={{
                    padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem',
                    background: themeColors.inputBg,
                    color: themeColors.text,
                    border: `1px solid ${themeColors.border}`,
                    borderRadius: '0.5rem',
                    fontFamily: "'Sora', sans-serif",
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    colorScheme: theme === 'dark' ? 'dark' : 'light'
                  }}
                  title={t('selectDate')}
                  aria-label={t('selectDate')}
                />
                {selectedDate.toDateString() !== new Date().toDateString() && (
                  <button
                    onClick={() => {
                      hapticFeedback('light');
                      setSelectedDate(new Date());
                    }}
                    style={{
                      padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem',
                      background: 'rgba(59, 130, 246, 0.1)',
                      color: '#60a5fa',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '0.5rem',
                      fontFamily: "'Sora', sans-serif",
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                    title={t('today')}
                    aria-label={t('today')}
                  >
                    {t('today')}
                  </button>
                )}
              </div>
              <button
                onClick={() => {
                  setShowHelp(true);
                  requestAnimationFrame(() => hapticFeedback('light'));
                }}
                style={{
                  padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem',
                  background: themeColors.inputBg,
                  color: themeColors.text,
                  border: `1px solid ${themeColors.border}`,
                  borderRadius: '0.5rem',
                  fontFamily: "'Sora', sans-serif",
                  fontSize: '0.875rem'
                }}
                title="Keyboard shortcuts (Press ? or /)"
                aria-label="Show keyboard shortcuts"
              >
                ⌨️ {!isMobile && t('help')}
              </button>
              <button
                onClick={() => {
                  hapticFeedback('light');
                  setTheme(theme === 'dark' ? 'light' : 'dark');
                }}
                style={{
                  padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem',
                  background: themeColors.inputBg,
                  color: themeColors.text,
                  border: `1px solid ${themeColors.border}`,
                  borderRadius: '0.5rem',
                  fontFamily: "'Sora', sans-serif",
                  fontSize: '0.875rem'
                }}
                title="Toggle theme (Press T)"
                aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              >
                {theme === 'dark' ? '☀️' : '🌙'}
              </button>
              <button
                onClick={() => {
                  hapticFeedback('light');
                  setTimeFormat(timeFormat === '24' ? '12' : '24');
                }}
                style={{
                  padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem',
                  background: themeColors.inputBg,
                  color: themeColors.text,
                  border: `1px solid ${themeColors.border}`,
                  borderRadius: '0.5rem',
                  fontFamily: "'Space Mono', monospace",
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}
                title="Toggle time format"
                aria-label={`Switch to ${timeFormat === '24' ? '12' : '24'}-hour format`}
              >
                {timeFormat === '24' ? '24h' : '12h'}
              </button>
              <select
                value={i18n.language}
                onChange={(e) => {
                  hapticFeedback('light');
                  i18n.changeLanguage(e.target.value);
                }}
                style={{
                  padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem',
                  paddingRight: isMobile ? '1.5rem' : '2rem',
                  background: themeColors.inputBg,
                  color: themeColors.text,
                  border: `1px solid ${themeColors.border}`,
                  borderRadius: '0.5rem',
                  fontFamily: "'Sora', sans-serif",
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  marginLeft: isMobile ? '0' : '0.25rem'
                }}
                title={t('language')}
                aria-label={t('language')}
              >
                <option value="en">🇬🇧 EN</option>
                <option value="es">🇪🇸 ES</option>
                <option value="fr">🇫🇷 FR</option>
                <option value="de">🇩🇪 DE</option>
                <option value="ja">🇯🇵 JP</option>
                <option value="zh">🇨🇳 ZH</option>
                <option value="pt">🇵🇹 PT</option>
              </select>
              {isMobile && locations.length > 1 && (
                <button
                  onClick={() => {
                    hapticFeedback('light');
                    if (showBestTimes) {
                      setShowBestTimes(false);
                      setBestMeetingTimes([]);
                    } else {
                      findBestMeetingTimes();
                    }
                  }}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: showBestTimes ? 'rgba(168, 85, 247, 0.1)' : 'rgba(168, 85, 247, 0.05)',
                    color: showBestTimes ? '#a855f7' : '#c084fc',
                    border: showBestTimes ? '1px solid rgba(168, 85, 247, 0.3)' : `1px solid ${themeColors.border}`,
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                  title={showBestTimes ? t('hideBestTimes') : t('findBestTime')}
                  aria-label={showBestTimes ? t('hideBestTimes') : t('findBestTime')}
                >
                  {showBestTimes ? '✓' : '🎯'}
                </button>
              )}
              {isMobile && locations.length > 0 && (
                <button
                  onClick={() => {
                    hapticFeedback('light');
                    shareURL();
                  }}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: 'rgba(59, 130, 246, 0.1)',
                    color: '#60a5fa',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem'
                  }}
                  title="Share URL"
                  aria-label="Share URL"
                >
                  🔗
                </button>
              )}
              {!isMobile && locations.length > 0 && (
                <button
                  onClick={() => setShowPresetModal(true)}
                  style={{
                    padding: '0.75rem 1rem',
                    background: 'rgba(251, 191, 36, 0.1)',
                    color: '#fbbf24',
                    border: '1px solid rgba(251, 191, 36, 0.3)',
                    borderRadius: '0.5rem',
                    fontFamily: "'Sora', sans-serif",
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                  title="Save current locations as preset"
                  aria-label="Save preset"
                >
                  💾 {t('savePreset')}
                </button>
              )}
              {!isMobile && presets.length > 0 && (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        const preset = presets.find(p => p.id === parseInt(e.target.value));
                        if (preset) loadPreset(preset);
                        e.target.value = '';
                      }
                    }}
                    style={{
                      padding: '0.75rem 2rem 0.75rem 1rem',
                      background: 'rgba(59, 130, 246, 0.1)',
                      color: '#60a5fa',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '0.5rem',
                      fontFamily: "'Sora', sans-serif",
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2360a5fa' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.5rem center',
                      backgroundSize: '1rem'
                    }}
                    aria-label="Load preset"
                  >
                    <option value="">📁 Load Preset</option>
                    {presets.map(preset => (
                      <option key={preset.id} value={preset.id}>
                        {preset.name} ({preset.locations.length} locations)
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {!isMobile && locations.length > 1 && (
                <button
                  onClick={() => {
                    if (showBestTimes) {
                      setShowBestTimes(false);
                      setBestMeetingTimes([]);
                    } else {
                      findBestMeetingTimes();
                    }
                  }}
                  style={{
                    padding: '0.75rem 1rem',
                    background: showBestTimes ? 'rgba(34, 197, 94, 0.1)' : 'rgba(168, 85, 247, 0.1)',
                    color: showBestTimes ? '#22c55e' : '#a855f7',
                    border: showBestTimes ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(168, 85, 247, 0.3)',
                    borderRadius: '0.5rem',
                    fontFamily: "'Sora', sans-serif",
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                  title={showBestTimes ? t('hideBestTimes') : t('findBestTime')}
                  aria-label={showBestTimes ? t('hideBestTimes') : t('findBestTime')}
                >
                  {showBestTimes ? `✓ ${t('showBestTimes')}` : `🎯 ${t('findBestTime')}`}
                </button>
              )}
              {!isMobile && locations.length > 0 && (
                <select
                  value={meetingDuration}
                  onChange={(e) => {
                    setMeetingDuration(parseInt(e.target.value));
                    hapticFeedback('light');
                  }}
                  style={{
                    padding: '0.75rem 2rem 0.75rem 1rem',
                    background: themeColors.inputBg,
                    color: themeColors.text,
                    border: `1px solid ${themeColors.border}`,
                    borderRadius: '0.5rem',
                    fontFamily: "'Sora', sans-serif",
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${theme === 'dark' ? '%23ffffff' : '%230f172a'}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.5rem center',
                    backgroundSize: '1rem'
                  }}
                  title={t('meetingDuration')}
                  aria-label={t('meetingDuration')}
                >
                  <option value="15">⏱️ 15 {t('minutes')}</option>
                  <option value="30">⏱️ 30 {t('minutes')}</option>
                  <option value="45">⏱️ 45 {t('minutes')}</option>
                  <option value="60">⏱️ 1 {i18n.language === 'en' ? 'hour' : t('hours')}</option>
                  <option value="90">⏱️ 1.5 {t('hours')}</option>
                  <option value="120">⏱️ 2 {t('hours')}</option>
                  <option value="240">⏱️ 4 {t('hours')}</option>
                </select>
              )}
              {!isMobile && showBestTimes && bestMeetingTimes.length > 0 && (
                <button
                  onClick={() => setShowCalendarModal(true)}
                  style={{
                    padding: '0.75rem 1rem',
                    background: 'rgba(34, 197, 94, 0.1)',
                    color: '#22c55e',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '0.5rem',
                    fontFamily: "'Sora', sans-serif",
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                  title={t('addToCalendar')}
                  aria-label={t('addToCalendar')}
                >
                  📅 {t('addToCalendar')}
                </button>
              )}
              {!isMobile && (
                <button
                  onClick={shareURL}
                  style={{
                    padding: '0.75rem 1rem',
                    background: 'rgba(59, 130, 246, 0.1)',
                    color: '#60a5fa',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '0.5rem',
                    fontFamily: "'Sora', sans-serif",
                    fontSize: '0.875rem'
                  }}
                  title={t('share')}
                  aria-label={t('share')}
                >
                  🔗 {t('share')}
                </button>
              )}
            </div>
          </div>
          <p style={{
            fontSize: isMobile ? '0.9375rem' : '1.125rem',
            color: themeColors.textMuted,
            margin: 0,
            fontFamily: "'Sora', sans-serif",
            fontWeight: '300'
          }}>
            {t('tagline')}. {t('taglineWithCount', { count: locations.length })}.
          </p>
        </header>

        {showAdd && !isMobile ? (
          <section className="glass-panel" style={{
            padding: '2rem',
            borderRadius: '1rem',
            marginBottom: '2rem',
            position: 'relative',
            zIndex: 10001
          }}>
            <div style={{ width: '100%', position: 'relative' }}>
              <label htmlFor="city-search" style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: themeColors.textMuted,
                fontSize: '0.875rem',
                fontFamily: "'Sora', sans-serif",
                fontWeight: '600'
              }}>
                {t('searchForCity')}
              </label>
              <div style={{ position: 'relative' }}>
                <Search size={20} style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: themeColors.textDim,
                  pointerEvents: 'none'
                }} />
                <input
                  id="city-search"
                  type="text"
                  placeholder={t('search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  autoComplete="off"
                  aria-label="Search for a city, country, or timezone"
                  aria-describedby="search-instructions"
                  role="combobox"
                  aria-expanded={filteredCities.length > 0}
                  aria-controls="city-results"
                  aria-activedescendant={filteredCities.length > 0 ? `city-option-${selectedIndex}` : undefined}
                  style={{
                    width: '100%',
                    paddingLeft: '3rem',
                    background: themeColors.inputBg,
                    color: themeColors.text,
                    border: `1px solid ${themeColors.border}`
                  }}
                />
              </div>

              {/* Favorites and Suggested quick-select */}
              {searchQuery.trim() === '' && (
                <>
                  {favorites.length > 0 && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <div style={{
                        fontSize: '0.75rem',
                        fontFamily: "'Space Mono', monospace",
                        color: themeColors.textMuted,
                        marginBottom: '0.75rem',
                        fontWeight: '700'
                      }}>
                        ⭐ FAVORITES
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {favorites.map(timezone => {
                          const cityName = Object.keys(cityTimezoneMap).find(
                            city => cityTimezoneMap[city] === timezone
                          );
                          return cityName ? (
                            <button
                              key={timezone}
                              onClick={() => addLocationFromCity(cityName, timezone)}
                              style={{
                                padding: '0.5rem 1rem',
                                background: 'rgba(251, 191, 36, 0.1)',
                                color: '#fbbf24',
                                border: '1px solid rgba(251, 191, 36, 0.3)',
                                borderRadius: '0.5rem',
                                fontFamily: "'Sora', sans-serif",
                                fontSize: '0.8125rem',
                                cursor: 'pointer'
                              }}
                              title={`Add ${cityName}`}
                            >
                              ⭐ {cityName}
                            </button>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}

                  {userLocationSuggestion && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <div style={{
                        fontSize: '0.75rem',
                        fontFamily: "'Space Mono', monospace",
                        color: themeColors.textMuted,
                        marginBottom: '0.75rem',
                        fontWeight: '700'
                      }}>
                        📍 YOUR LOCATION
                      </div>
                      <button
                        onClick={() => addLocationFromCity(userLocationSuggestion.city, userLocationSuggestion.timezone)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'rgba(34, 197, 94, 0.1)',
                          color: '#22c55e',
                          border: '1px solid rgba(34, 197, 94, 0.3)',
                          borderRadius: '0.5rem',
                          fontFamily: "'Sora', sans-serif",
                          fontSize: '0.8125rem',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                        title={`Add ${userLocationSuggestion.city} (Your current timezone)`}
                      >
                        📍 {userLocationSuggestion.city}
                      </button>
                    </div>
                  )}

                  <div style={{ marginTop: '1.5rem' }}>
                    <div style={{
                      fontSize: '0.75rem',
                      fontFamily: "'Space Mono', monospace",
                      color: themeColors.textMuted,
                      marginBottom: '0.75rem',
                      fontWeight: '700'
                    }}>
                      🌍 POPULAR LOCATIONS
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {[
                        { city: 'New York', tz: 'America/New_York', abbrev: 'EST' },
                        { city: 'London', tz: 'Europe/London', abbrev: 'GMT' },
                        { city: 'Tokyo', tz: 'Asia/Tokyo', abbrev: 'JST' },
                        { city: 'Sydney', tz: 'Australia/Sydney', abbrev: 'AEST' },
                        { city: 'Los Angeles', tz: 'America/Los_Angeles', abbrev: 'PST' },
                        { city: 'Singapore', tz: 'Asia/Singapore', abbrev: 'SGT' },
                        { city: 'Dubai', tz: 'Asia/Dubai', abbrev: 'GST' },
                        { city: 'Mumbai', tz: 'Asia/Kolkata', abbrev: 'IST' }
                      ].map(({ city, tz, abbrev }) => (
                        <button
                          key={city}
                          onClick={() => addLocationFromCity(city, tz)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: 'rgba(59, 130, 246, 0.1)',
                            color: '#60a5fa',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            borderRadius: '0.5rem',
                            fontFamily: "'Sora', sans-serif",
                            fontSize: '0.8125rem',
                            cursor: 'pointer'
                          }}
                          title={`Add ${city} (${abbrev})`}
                        >
                          {city}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {filteredCities.length > 0 && (
                <div
                  id="city-results"
                  role="listbox"
                  aria-label="Search results"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '0.5rem',
                    background: themeColors.panelBgStrong,
                    border: `1px solid ${themeColors.border}`,
                    borderRadius: '0.75rem',
                    overflow: 'hidden',
                    zIndex: 10000,
                    boxShadow: theme === 'dark' ? '0 20px 40px rgba(0, 0, 0, 0.5)' : '0 20px 40px rgba(0, 0, 0, 0.15)'
                  }}
                >
                  {filteredCities.map((city, index) => {
                    const matchTypeBadge = {
                      timezone: { label: 'TZ', color: '#8b5cf6' },
                      country: { label: 'Country', color: '#10b981' },
                      city: { label: 'City', color: '#3b82f6' }
                    };
                    const badge = matchTypeBadge[city.matchType] || matchTypeBadge.city;

                    return (
                      <div
                        key={`${city.name}-${index}`}
                        id={`city-option-${index}`}
                        role="option"
                        aria-selected={index === selectedIndex}
                        onClick={() => addLocationFromCity(city.name, city.timezone)}
                        style={{
                          padding: '1rem 1.5rem',
                          cursor: 'pointer',
                          background: index === selectedIndex ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                          borderBottom: index < filteredCities.length - 1 ? `1px solid ${themeColors.border}` : 'none',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '1rem'
                        }}
                        onMouseEnter={() => setSelectedIndex(index)}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{
                            color: themeColors.text,
                            fontFamily: "'Sora', sans-serif",
                            fontWeight: '600',
                            marginBottom: '0.25rem'
                          }}>
                            {city.name}
                          </div>
                          <div style={{
                            color: themeColors.textMuted,
                            fontFamily: "'Space Mono', monospace",
                            fontSize: '0.75rem'
                          }}>
                            {city.timezone}
                          </div>
                        </div>
                        {city.matchType && (
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            background: `${badge.color}20`,
                            color: badge.color,
                            borderRadius: '0.25rem',
                            fontSize: '0.625rem',
                            fontWeight: '700',
                            fontFamily: "'Sora', sans-serif",
                            textTransform: 'uppercase'
                          }}>
                            {badge.label}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <button
              onClick={() => { setShowAdd(false); setSearchQuery(''); }}
              style={{
                marginTop: '1rem',
                padding: '0.75rem 1.5rem',
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '0.5rem',
                fontFamily: "'Sora', sans-serif"
              }}
            >
              {t('cancel')}
            </button>
          </section>
        ) : (
          <button
            onClick={() => setShowAdd(true)}
            style={{
              padding: '1rem 2rem',
              background: 'rgba(59, 130, 246, 0.1)',
              color: '#60a5fa',
              border: '2px dashed rgba(59, 130, 246, 0.3)',
              borderRadius: '1rem',
              marginBottom: '2rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              fontFamily: "'Sora', sans-serif",
              ...(isMobile && { width: '100%' })
            }}
          >
            <Plus size={20} />
            {t('addLocation')}
          </button>
        )}

        {isLoading ? (
          <section className="glass-panel" style={{
            borderRadius: '1rem',
            overflow: 'hidden',
            padding: '1rem'
          }}>
            {/* Skeleton loading UI */}
            {[1, 2].map((i) => (
              <div key={i} style={{
                display: 'flex',
                gap: '1rem',
                padding: '1.5rem',
                borderBottom: i === 1 ? `1px solid ${themeColors.border}` : 'none'
              }}>
                <div style={{
                  width: isMobile ? '140px' : '200px',
                  height: '120px',
                  background: themeColors.panelBg,
                  borderRadius: '0.5rem',
                  animation: 'pulse 2s ease-in-out infinite'
                }} />
                {!isMobile && (
                  <div style={{ flex: 1, display: 'flex', gap: '0.5rem' }}>
                    {Array.from({ length: 12 }).map((_, j) => (
                      <div key={j} style={{
                        flex: 1,
                        height: '120px',
                        background: themeColors.panelBg,
                        borderRadius: '0.25rem',
                        animation: `pulse 2s ease-in-out infinite ${j * 0.1}s`
                      }} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </section>
        ) : locations.length === 0 ? (
          <section className="glass-panel" style={{
            padding: isMobile ? '2rem 1.5rem' : '4rem 3rem',
            borderRadius: '1rem',
            textAlign: 'center'
          }}>
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem', animation: 'pulse 2s ease-in-out infinite' }}>🌍</div>
              <h3 style={{
                fontSize: isMobile ? '1.5rem' : '2rem',
                fontWeight: '700',
                color: themeColors.text,
                marginBottom: '1rem',
                fontFamily: "'Sora', sans-serif"
              }}>
                Welcome to Clockbridge!
              </h3>
              <p style={{
                fontSize: '1rem',
                color: themeColors.textMuted,
                marginBottom: '2rem',
                fontFamily: "'Sora', sans-serif",
                lineHeight: '1.6'
              }}>
                Your smart timezone visualizer for distributed teams. Find the perfect meeting time across any timezone in seconds.
              </p>

              {/* Quick Start Section */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: '1rem',
                marginBottom: '2rem',
                textAlign: 'left'
              }}>
                <div style={{
                  padding: '1rem',
                  background: themeColors.panelBg,
                  borderRadius: '0.5rem',
                  border: `1px solid ${themeColors.border}`
                }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🕒</div>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: themeColors.text, marginBottom: '0.25rem', fontFamily: "'Sora', sans-serif" }}>
                    Add Locations
                  </div>
                  <div style={{ fontSize: '0.75rem', color: themeColors.textMuted, fontFamily: "'Sora', sans-serif", lineHeight: '1.4' }}>
                    Search 150+ cities worldwide or use timezone codes like PST, EST
                  </div>
                </div>
                <div style={{
                  padding: '1rem',
                  background: themeColors.panelBg,
                  borderRadius: '0.5rem',
                  border: `1px solid ${themeColors.border}`
                }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🎯</div>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: themeColors.text, marginBottom: '0.25rem', fontFamily: "'Sora', sans-serif" }}>
                    Find Best Time
                  </div>
                  <div style={{ fontSize: '0.75rem', color: themeColors.textMuted, fontFamily: "'Sora', sans-serif", lineHeight: '1.4' }}>
                    Automatically discover meeting times that work for everyone
                  </div>
                </div>
                <div style={{
                  padding: '1rem',
                  background: themeColors.panelBg,
                  borderRadius: '0.5rem',
                  border: `1px solid ${themeColors.border}`
                }}>
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>💾</div>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: themeColors.text, marginBottom: '0.25rem', fontFamily: "'Sora', sans-serif" }}>
                    {t('savePresetsHelp')}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: themeColors.textMuted, fontFamily: "'Sora', sans-serif", lineHeight: '1.4' }}>
                    {t('savePresetsHelpDescription')}
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={() => setShowAdd(true)}
                style={{
                  padding: '1rem 2rem',
                  background: 'rgba(59, 130, 246, 0.2)',
                  color: '#60a5fa',
                  border: '2px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '0.75rem',
                  fontFamily: "'Sora', sans-serif",
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '1.5rem'
                }}
                aria-label="Add first location"
              >
                <Plus size={20} />
                Add Your First Location
              </button>

              {/* Quick Add Popular Timezones */}
              {!isMobile && (
                <div style={{
                  marginBottom: '2rem',
                  padding: '1rem',
                  background: themeColors.panelBg,
                  borderRadius: '0.5rem',
                  textAlign: 'left'
                }}>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: themeColors.text,
                    marginBottom: '0.75rem',
                    fontFamily: "'Sora', sans-serif"
                  }}>
                    ⚡ Quick Start - Popular Locations:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {[
                      { name: 'New York', tz: 'America/New_York' },
                      { name: 'London', tz: 'Europe/London' },
                      { name: 'Tokyo', tz: 'Asia/Tokyo' },
                      { name: 'Sydney', tz: 'Australia/Sydney' },
                      { name: 'San Francisco', tz: 'America/Los_Angeles' },
                      { name: 'Singapore', tz: 'Asia/Singapore' }
                    ].map(city => (
                      <button
                        key={city.tz}
                        onClick={() => addLocationFromCity(city.name, city.tz)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          background: 'rgba(59, 130, 246, 0.1)',
                          color: '#60a5fa',
                          border: `1px solid rgba(59, 130, 246, 0.2)`,
                          borderRadius: '0.375rem',
                          fontFamily: "'Sora', sans-serif",
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}
                      >
                        + {city.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Keyboard Shortcuts */}
              <div style={{ padding: '1rem', background: themeColors.panelBg, borderRadius: '0.5rem', textAlign: 'left' }}>
                <p style={{
                  color: themeColors.textMuted,
                  fontSize: '0.875rem',
                  margin: 0,
                  fontFamily: "'Sora', sans-serif"
                }}>
                  ⌨️ <strong>Keyboard shortcuts:</strong> Press <kbd style={{
                    padding: '0.25rem 0.5rem',
                    background: themeColors.inputBg,
                    borderRadius: '0.25rem',
                    fontFamily: "'Space Mono', monospace",
                    border: `1px solid ${themeColors.border}`,
                    marginLeft: '0.25rem',
                    marginRight: '0.25rem'
                  }}>N</kbd> to add location • <kbd style={{
                    padding: '0.25rem 0.5rem',
                    background: themeColors.inputBg,
                    borderRadius: '0.25rem',
                    fontFamily: "'Space Mono', monospace",
                    border: `1px solid ${themeColors.border}`,
                    marginLeft: '0.25rem',
                    marginRight: '0.25rem'
                  }}>T</kbd> to toggle theme • <kbd style={{
                    padding: '0.25rem 0.5rem',
                    background: themeColors.inputBg,
                    borderRadius: '0.25rem',
                    fontFamily: "'Space Mono', monospace",
                    border: `1px solid ${themeColors.border}`,
                    marginLeft: '0.25rem',
                    marginRight: '0.25rem'
                  }}>?</kbd> for help
                </p>
              </div>
            </div>
          </section>
        ) : (
          <>
            {/* Business Hours Legend */}
            {!isMobile && locations.length > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1.5rem',
                padding: '0.75rem 1rem',
                background: themeColors.panelBg,
                borderRadius: '0.5rem',
                marginBottom: '1rem',
                fontSize: '0.8125rem',
                fontFamily: "'Sora', sans-serif"
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    background: `${colorPalette[0]}30`,
                    borderRadius: '0.25rem',
                    border: `1px solid ${colorPalette[0]}50`
                  }} />
                  <span style={{ color: themeColors.textMuted }}>{t('businessHours')}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    background: 'linear-gradient(135deg, #a855f740, #ec489950)',
                    borderRadius: '0.25rem',
                    border: '2px solid #a855f7'
                  }} />
                  <span style={{ color: themeColors.textMuted }}>{t('optimalMeetingTime')}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    background: 'transparent',
                    borderRadius: '0.25rem',
                    border: `1px solid ${themeColors.border}`
                  }} />
                  <span style={{ color: themeColors.textMuted }}>{t('offHours')}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    background: theme === 'dark' ? 'rgba(251, 191, 36, 0.2)' : 'rgba(251, 191, 36, 0.3)',
                    borderRadius: '0.25rem',
                    border: `1px solid ${theme === 'dark' ? '#fbbf24' : '#f59e0b'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.5rem',
                    fontWeight: '700',
                    color: theme === 'dark' ? '#fbbf24' : '#f59e0b',
                    fontFamily: "'Space Mono', monospace"
                  }}>
                    WE
                  </div>
                  <span style={{ color: themeColors.textMuted }}>{t('weekend')}</span>
                </div>
              </div>
            )}

            {/* Top Banner Ad */}
            <AdBanner
              type="carbon"
              slot="1234567890"
              format="horizontal"
              show={showTopAd}
              onClose={() => setShowTopAd(false)}
              isMobile={isMobile}
              theme={theme}
              themeColors={themeColors}
            />

            <section className="glass-panel" style={{ borderRadius: '1rem', overflow: 'auto', position: 'relative' }}>
            {/* Scroll indicator for mobile */}
            {isMobile && (
              <div style={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: '40px',
                background: theme === 'dark'
                  ? 'linear-gradient(to left, rgba(15, 23, 42, 0.95), transparent)'
                  : 'linear-gradient(to left, rgba(255, 255, 255, 0.95), transparent)',
                pointerEvents: 'none',
                zIndex: 50,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                paddingRight: '0.5rem'
              }}>
                <span style={{
                  color: themeColors.textMuted,
                  fontSize: '1.25rem',
                  opacity: 0.5,
                  animation: 'pulse 2s ease-in-out infinite'
                }}>›</span>
              </div>
            )}
            <div style={{
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: isMobile ? 'none' : 'thin',
              msOverflowStyle: isMobile ? 'none' : 'auto',
              scrollSnapType: isMobile ? 'x proximity' : 'none'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '140px repeat(24, 80px)' : '200px repeat(24, minmax(70px, 1fr))',
                borderBottom: `1px solid ${themeColors.border}`,
                background: themeColors.headerBg,
                minWidth: isMobile ? '2060px' : '1880px',
                ...(isMobile && {
                  position: 'sticky',
                  top: 0,
                  zIndex: 10
                })
              }}>
              <div style={{
                padding: isMobile ? '1rem 1rem' : '1.5rem',
                color: themeColors.textMuted,
                fontFamily: "'Space Mono', monospace",
                fontSize: isMobile ? '0.875rem' : '0.75rem',
                fontWeight: '700',
                position: 'sticky',
                left: 0,
                background: themeColors.headerBg,
                zIndex: 11
              }}>
                LOCATION
              </div>
              {hours.map(hour => (
                <div key={hour} style={{ padding: isMobile ? '1rem 0.5rem' : '1rem 0.25rem', textAlign: 'center', color: themeColors.textDim, fontFamily: "'Space Mono', monospace", fontSize: isMobile ? '0.875rem' : '0.75rem', fontWeight: isMobile ? '600' : 'normal' }}>
                  {hour.toString().padStart(2, '0')}
                </div>
              ))}
              </div>

              {locations.map((location, index) => {
              const currentHour = getCurrentHourForLocation(location.timezone);
              const primary = getPrimaryLocation;

              return (
                <div key={location.id} style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '140px repeat(24, 80px)' : '200px repeat(24, minmax(70px, 1fr))',
                  borderBottom: index < locations.length - 1 ? `1px solid ${themeColors.border}` : 'none',
                  position: 'relative',
                  minWidth: isMobile ? '2060px' : '1880px'
                }}>
                  {/* Delete button revealed on swipe */}
                  {isMobile && swipedLocation === location.id && (
                    <div style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '140px',
                      background: '#dc2626',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      paddingRight: '1rem',
                      zIndex: 8
                    }}>
                      <button
                        onClick={() => removeLocation(location.id)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'white',
                          fontSize: '1.5rem',
                          cursor: 'pointer',
                          padding: '0.5rem'
                        }}
                        aria-label="Delete location"
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                  <div
                    data-location-id={location.id}
                    onTouchStart={(e) => handleLocationTouchStart(e, location.id)}
                    onTouchMove={(e) => handleLocationTouchMove(e, location.id)}
                    onTouchEnd={(e) => handleLocationTouchEnd(e, location.id)}
                    style={{
                      padding: isMobile ? '1.25rem 1rem' : '1.5rem',
                      paddingTop: location.isPrimary ? (isMobile ? '2rem' : '2.5rem') : (isMobile ? '1.25rem' : '1.5rem'),
                      display: 'flex',
                      flexDirection: 'column',
                      gap: isMobile ? '1rem' : '0.75rem',
                      borderRight: `1px solid ${themeColors.border}`,
                      background: location.isPrimary
                        ? (theme === 'dark' ? `linear-gradient(to right, ${location.color}40, rgb(15, 23, 42))` : `linear-gradient(to right, ${location.color}50, rgb(255, 255, 255))`)
                        : (theme === 'dark' ? 'rgb(15, 23, 42)' : 'rgb(255, 255, 255)'),
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      boxShadow: '2px 0 8px rgba(0, 0, 0, 0.15)',
                      position: 'sticky',
                      left: 0,
                      zIndex: 100,
                      transition: isMobile ? 'transform 0.3s ease' : 'none',
                      touchAction: isMobile ? 'pan-y' : 'auto'
                    }}>
                    {/* Location name and details */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: location.color, boxShadow: `0 0 10px ${location.color}`, flexShrink: 0 }} />
                          <span style={{ color: themeColors.text, fontFamily: "'Sora', sans-serif", fontWeight: '600', fontSize: isMobile ? '0.9375rem' : '1rem', flex: isMobile ? '1 1 auto' : 'none' }}>
                            {location.name}
                          </span>
                          {!isMobile && location.isPrimary && (
                            <div style={{
                              padding: '0.125rem 0.375rem',
                              background: location.color,
                              color: 'white',
                              borderRadius: '0.25rem',
                              fontSize: '0.5625rem',
                              fontWeight: '700',
                              fontFamily: "'Space Mono', monospace",
                              letterSpacing: '0.05em'
                            }}>
                              PRIMARY
                            </div>
                          )}
                        </div>
                        <div style={{ paddingLeft: isMobile ? '0' : '1.25rem', position: 'relative', display: 'flex', alignItems: 'center', gap: isMobile ? '0.375rem' : '0.5rem', justifyContent: 'space-between', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: isMobile ? '0 0 auto' : '1' }}>
                            <span
                              style={{
                                color: location.color,
                                fontFamily: "'Space Mono', monospace",
                                fontSize: isMobile ? '0.6875rem' : '0.6875rem',
                                fontWeight: '700',
                                background: `${location.color}15`,
                                padding: isMobile ? '0.1875rem 0.375rem' : '0.125rem 0.375rem',
                                borderRadius: '0.25rem',
                                cursor: 'help',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={() => !isMobile && setTooltipLocation(location)}
                              onMouseLeave={() => setTooltipLocation(null)}
                              title={`Click for more info about ${location.timezone}`}
                            >
                              {getUTCOffset(location.timezone)}
                            </span>
                            {isMobile && location.isPrimary && (
                              <div style={{
                                padding: '0.1875rem 0.375rem',
                                background: location.color,
                                color: 'white',
                                borderRadius: '0.25rem',
                                fontSize: '0.5rem',
                                fontWeight: '700',
                                fontFamily: "'Space Mono', monospace",
                                letterSpacing: '0.05em',
                                flexShrink: 0
                              }}>
                                PRIMARY
                              </div>
                            )}
                          </div>

                          {/* Star and X buttons */}
                          <div style={{
                            display: 'flex',
                            gap: '0.25rem',
                            alignItems: 'center',
                            flexShrink: 0
                          }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(location.timezone);
                              }}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: favorites.includes(location.timezone) ? '#fbbf24' : themeColors.textMuted,
                                padding: isMobile ? '0.25rem' : '0.25rem',
                                cursor: 'pointer',
                                borderRadius: '0.25rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: isMobile ? '32px' : '32px',
                                minHeight: isMobile ? '32px' : '32px',
                                transition: 'all 0.15s ease',
                                fontSize: isMobile ? '0.875rem' : '0.75rem',
                                opacity: favorites.includes(location.timezone) ? 1 : 0.6,
                                touchAction: 'manipulation'
                              }}
                              onMouseEnter={(e) => {
                                if (!isMobile) {
                                  e.currentTarget.style.opacity = '1';
                                  e.currentTarget.style.background = favorites.includes(location.timezone)
                                    ? 'rgba(251, 191, 36, 0.1)'
                                    : (theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)');
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = favorites.includes(location.timezone) ? '1' : '0.6';
                                e.currentTarget.style.background = 'transparent';
                              }}
                              title={favorites.includes(location.timezone) ? 'Remove from favorites' : 'Add to favorites'}
                              aria-label={favorites.includes(location.timezone) ? 'Remove from favorites' : 'Add to favorites'}
                            >
                              {favorites.includes(location.timezone) ? '⭐' : '☆'}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeLocation(location.id);
                              }}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: themeColors.textMuted,
                                padding: isMobile ? '0.25rem' : '0.25rem',
                                cursor: 'pointer',
                                borderRadius: '0.25rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: isMobile ? '32px' : '32px',
                                minHeight: isMobile ? '32px' : '32px',
                                transition: 'all 0.15s ease',
                                opacity: 0.6,
                                touchAction: 'manipulation'
                              }}
                              onMouseEnter={(e) => {
                                if (!isMobile) {
                                  e.currentTarget.style.opacity = '1';
                                  e.currentTarget.style.color = '#ef4444';
                                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '0.6';
                                e.currentTarget.style.color = themeColors.textMuted;
                                e.currentTarget.style.background = 'transparent';
                              }}
                              aria-label="Remove location"
                            >
                              <X size={isMobile ? 18 : 12} />
                            </button>
                          </div>
                          {!isMobile && tooltipLocation?.id === location.id && (() => {
                            const details = getTimezoneDetails(location.timezone);
                            return (
                              <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                marginTop: '0.5rem',
                                background: themeColors.panelBgStrong,
                                border: `2px solid ${location.color}`,
                                borderRadius: '0.5rem',
                                padding: '0.75rem',
                                minWidth: '280px',
                                boxShadow: theme === 'dark' ? '0 10px 30px rgba(0, 0, 0, 0.5)' : '0 10px 30px rgba(0, 0, 0, 0.2)',
                                zIndex: 1000,
                                pointerEvents: 'none'
                              }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                  <div style={{
                                    fontSize: '0.75rem',
                                    fontWeight: '700',
                                    color: location.color,
                                    fontFamily: "'Sora', sans-serif",
                                    borderBottom: `1px solid ${themeColors.border}`,
                                    paddingBottom: '0.5rem'
                                  }}>
                                    {details.timezone}
                                  </div>
                                  <div style={{
                                    fontSize: '0.6875rem',
                                    color: themeColors.text,
                                    fontFamily: "'Space Mono', monospace"
                                  }}>
                                    <div style={{ marginBottom: '0.25rem' }}>
                                      <span style={{ color: themeColors.textMuted }}>Time: </span>
                                      {details.localTime}
                                    </div>
                                    <div style={{ marginBottom: '0.25rem' }}>
                                      <span style={{ color: themeColors.textMuted }}>Offset: </span>
                                      {details.offset}
                                    </div>
                                    {details.cities.length > 0 && (
                                      <div>
                                        <span style={{ color: themeColors.textMuted }}>Major cities: </span>
                                        {details.cities.join(', ')}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    <div style={{ color: location.color, fontFamily: "'Space Mono', monospace", fontSize: '0.875rem', fontWeight: '700' }}>
                      {formatTime(getTimeForLocation(location.timezone))}
                    </div>
                    {editingHours === location.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <label style={{ fontSize: '0.625rem', color: themeColors.textMuted, fontFamily: "'Sora', sans-serif" }}>{t('start')}</label>
                            <input
                              type="time"
                              defaultValue={`${(location.businessHourStart ?? 9).toString().padStart(2, '0')}:00`}
                              id={`start-${location.id}`}
                              style={{
                                width: '110px',
                                padding: '0.5rem 0.75rem',
                                fontSize: '0.875rem',
                                background: themeColors.inputBg,
                                color: themeColors.text,
                                border: `2px solid ${themeColors.border}`,
                                borderRadius: '0.5rem',
                                fontFamily: "'Space Mono', monospace",
                                fontWeight: '600',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer'
                              }}
                            />
                          </div>
                          <span style={{ color: themeColors.textMuted, fontSize: '0.75rem', marginTop: '1rem' }}>-</span>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <label style={{ fontSize: '0.625rem', color: themeColors.textMuted, fontFamily: "'Sora', sans-serif" }}>{t('end')}</label>
                            <input
                              type="time"
                              defaultValue={`${(location.businessHourEnd ?? 17).toString().padStart(2, '0')}:00`}
                              id={`end-${location.id}`}
                              style={{
                                width: '110px',
                                padding: '0.5rem 0.75rem',
                                fontSize: '0.875rem',
                                background: themeColors.inputBg,
                                color: themeColors.text,
                                border: `2px solid ${themeColors.border}`,
                                borderRadius: '0.5rem',
                                fontFamily: "'Space Mono', monospace",
                                fontWeight: '600',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer'
                              }}
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => {
                              const startTime = document.getElementById(`start-${location.id}`).value;
                              const endTime = document.getElementById(`end-${location.id}`).value;
                              const start = parseInt(startTime.split(':')[0]);
                              const end = parseInt(endTime.split(':')[0]);
                              updateBusinessHours(location.id, start, end);
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: location.color,
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem'
                            }}
                          >
                            {t('save')}
                          </button>
                          <button
                            onClick={() => setEditingHours(null)}
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: themeColors.panelBgStrong,
                              color: themeColors.text,
                              border: `1px solid ${themeColors.border}`,
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem'
                            }}
                          >
                            {t('cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditingHours(location.id)}
                        style={{
                          padding: '0.5rem',
                          background: themeColors.panelBg,
                          color: themeColors.textMuted,
                          border: `1px solid ${themeColors.border}`,
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          fontFamily: "'Sora', sans-serif"
                        }}
                      >
                        📋 {t('hoursLabel')}: {location.businessHourStart ?? 9}:00 - {location.businessHourEnd ?? 17}:00
                      </button>
                    )}
                    {!location.isPrimary && (
                      <button
                        onClick={() => setPrimaryLocation(location.id)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: `${location.color}30`,
                          color: 'white',
                          border: `1px solid ${location.color}40`,
                          borderRadius: '0.375rem',
                          fontFamily: "'Sora', sans-serif",
                          fontSize: '0.75rem'
                        }}
                        title={t('makePrimary')}
                        aria-label={t('makePrimary')}
                      >
                        {t('makePrimary')}
                      </button>
                    )}
                  </div>

                  {hours.map(primaryHour => {
                    if (location.isPrimary) {
                      const isCurrentHour = primaryHour === currentHour;
                      const isBusiness = isBusinessHour(primaryHour, location, location.timezone);
                      const timeAndDay = getTimeForHourInTimezone(location.timezone, primaryHour);
                      const isBestTime = showBestTimes && bestMeetingTimes.some(bt => bt.primaryHour === primaryHour);
                      const isWeekendHour = isWeekend(primaryHour, location.timezone);
                      const holidayInfo = getHolidayForHour(primaryHour, location.timezone);

                      return (
                        <div key={primaryHour} className="hour-cell" style={{
                          padding: '1rem 0.25rem',
                          background: isBestTime
                            ? `linear-gradient(135deg, #a855f740, #ec489950)`
                            : (isBusiness ? `${location.color}${theme === 'dark' ? '20' : '30'}` : 'transparent'),
                          borderLeft: isCurrentHour ? `3px solid ${location.color}` : `1px solid ${themeColors.border}`,
                          borderTop: isBestTime ? '2px solid #a855f7' : undefined,
                          borderRight: isBestTime ? '2px solid #a855f7' : undefined,
                          borderBottom: isBestTime ? '2px solid #a855f7' : undefined,
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: '60px',
                          boxShadow: isBestTime ? 'inset 0 0 20px rgba(168, 85, 247, 0.2)' : 'none'
                        }}>
                          {isCurrentHour && (
                            <div className="current-time-indicator" style={{ position: 'absolute', top: '0', left: '0', right: '0', height: '3px', background: location.color }}>
                              <div style={{
                                position: 'absolute',
                                top: '4px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: location.color,
                                color: 'white',
                                padding: '0.125rem 0.375rem',
                                borderRadius: '0.25rem',
                                fontSize: '0.5rem',
                                fontWeight: '700',
                                fontFamily: "'Space Mono', monospace",
                                whiteSpace: 'nowrap',
                                boxShadow: `0 2px 8px ${location.color}80`,
                                zIndex: 5
                              }}>
                                NOW
                              </div>
                            </div>
                          )}
                          {isBestTime && (
                            <div style={{
                              position: 'absolute',
                              top: '0.25rem',
                              left: '0.25rem',
                              background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                              color: 'white',
                              padding: '0.125rem 0.3rem',
                              borderRadius: '0.25rem',
                              fontSize: '0.5rem',
                              fontWeight: '700',
                              fontFamily: "'Space Mono', monospace",
                              letterSpacing: '0.05em',
                              boxShadow: '0 2px 4px rgba(168, 85, 247, 0.3)'
                            }}>
                              {t('bestShort')}
                            </div>
                          )}
                          {isWeekendHour && (
                            <div style={{
                              position: 'absolute',
                              top: '0.25rem',
                              right: '0.25rem',
                              background: theme === 'dark' ? 'rgba(251, 191, 36, 0.2)' : 'rgba(251, 191, 36, 0.3)',
                              color: theme === 'dark' ? '#fbbf24' : '#f59e0b',
                              padding: '0.125rem 0.25rem',
                              borderRadius: '0.25rem',
                              fontSize: '0.5rem',
                              fontWeight: '700',
                              fontFamily: "'Space Mono', monospace",
                              letterSpacing: '0.025em'
                            }}>
                              {t('weekendShort')}
                            </div>
                          )}
                          {holidayInfo && (
                            <div
                              style={{
                                position: 'absolute',
                                bottom: '0.25rem',
                                left: '0.25rem',
                                right: '0.25rem',
                                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                color: 'white',
                                padding: '0.125rem 0.25rem',
                                borderRadius: '0.25rem',
                                fontSize: '0.45rem',
                                fontWeight: '700',
                                fontFamily: "'Space Mono', monospace",
                                letterSpacing: '0.025em',
                                boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                                textAlign: 'center',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                              title={holidayInfo.name}
                            >
                              🏖️ {holidayInfo.name}
                            </div>
                          )}
                          <span style={{ color: isBusiness ? themeColors.textBright : themeColors.textDim, fontSize: '0.625rem', fontFamily: "'Space Mono', monospace", fontWeight: isBusiness ? '700' : '400' }}>
                            {timeAndDay.time}
                          </span>
                          <span style={{ color: isBusiness ? themeColors.textMuted : themeColors.textDim, fontSize: '0.5625rem', fontFamily: "'Space Mono', monospace" }}>
                            {timeAndDay.day}
                          </span>
                        </div>
                      );
                    }
                    
                    // Safety check: if no primary location, just show the time for this location
                    if (!primary) {
                      const timeAndDay = getTimeForHourInTimezone(location.timezone, primaryHour);
                      const isCurrentHour = primaryHour === currentHour;
                      const isBusiness = isBusinessHour(primaryHour, location, location.timezone);
                      const isWeekendHour = isWeekend(primaryHour, location.timezone);
                      const holidayInfo = getHolidayForHour(primaryHour, location.timezone);

                      return (
                        <div key={primaryHour} className="hour-cell" style={{
                          padding: '1rem 0.25rem',
                          background: isBusiness ? `${location.color}${theme === 'dark' ? '20' : '30'}` : 'transparent',
                          borderLeft: isCurrentHour ? `3px solid ${location.color}` : `1px solid ${themeColors.border}`,
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: '60px'
                        }}>
                          {isCurrentHour && (
                            <div className="current-time-indicator" style={{ position: 'absolute', top: '0', left: '0', right: '0', height: '3px', background: location.color }}>
                              <div style={{
                                position: 'absolute',
                                top: '4px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: location.color,
                                color: 'white',
                                padding: '0.125rem 0.375rem',
                                borderRadius: '0.25rem',
                                fontSize: '0.5rem',
                                fontWeight: '700',
                                fontFamily: "'Space Mono', monospace",
                                whiteSpace: 'nowrap',
                                boxShadow: `0 2px 8px ${location.color}80`,
                                zIndex: 5
                              }}>
                                NOW
                              </div>
                            </div>
                          )}
                          {isBestTime && (
                            <div style={{
                              position: 'absolute',
                              top: '0.25rem',
                              left: '0.25rem',
                              background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                              color: 'white',
                              padding: '0.125rem 0.3rem',
                              borderRadius: '0.25rem',
                              fontSize: '0.5rem',
                              fontWeight: '700',
                              fontFamily: "'Space Mono', monospace",
                              letterSpacing: '0.05em',
                              boxShadow: '0 2px 4px rgba(168, 85, 247, 0.3)'
                            }}>
                              {t('bestShort')}
                            </div>
                          )}
                          {isWeekendHour && (
                            <div style={{
                              position: 'absolute',
                              top: '0.25rem',
                              right: '0.25rem',
                              background: theme === 'dark' ? 'rgba(251, 191, 36, 0.2)' : 'rgba(251, 191, 36, 0.3)',
                              color: theme === 'dark' ? '#fbbf24' : '#f59e0b',
                              padding: '0.125rem 0.25rem',
                              borderRadius: '0.25rem',
                              fontSize: '0.5rem',
                              fontWeight: '700',
                              fontFamily: "'Space Mono', monospace",
                              letterSpacing: '0.025em'
                            }}>
                              {t('weekendShort')}
                            </div>
                          )}
                          {holidayInfo && (
                            <div
                              style={{
                                position: 'absolute',
                                bottom: '0.25rem',
                                left: '0.25rem',
                                right: '0.25rem',
                                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                color: 'white',
                                padding: '0.125rem 0.25rem',
                                borderRadius: '0.25rem',
                                fontSize: '0.45rem',
                                fontWeight: '700',
                                fontFamily: "'Space Mono', monospace",
                                letterSpacing: '0.025em',
                                boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                                textAlign: 'center',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                              title={holidayInfo.name}
                            >
                              🏖️ {holidayInfo.name}
                            </div>
                          )}
                          <span style={{ color: isBusiness ? themeColors.textBright : themeColors.textDim, fontSize: '0.625rem', fontFamily: "'Space Mono', monospace", fontWeight: isBusiness ? '700' : '400' }}>
                            {timeAndDay.time}
                          </span>
                          <span style={{ color: isBusiness ? themeColors.textMuted : themeColors.textDim, fontSize: '0.5625rem', fontFamily: "'Space Mono', monospace" }}>
                            {timeAndDay.day}
                          </span>
                        </div>
                      );
                    }

                    const correspondingTime = getCorrespondingTimeInTimezone(primary.timezone, location.timezone, primaryHour);
                    const timeMatch = correspondingTime.time.match(/(\d+):(\d+)\s*(AM|PM)/i);
                    let correspondingHour = 0;
                    if (timeMatch) {
                      correspondingHour = parseInt(timeMatch[1]);
                      if (timeMatch[3].toUpperCase() === 'PM' && correspondingHour !== 12) correspondingHour += 12;
                      else if (timeMatch[3].toUpperCase() === 'AM' && correspondingHour === 12) correspondingHour = 0;
                    }

                    const isBusiness = isBusinessHour(correspondingHour, location, location.timezone);
                    const isPrimaryBusiness = isBusinessHour(primaryHour, primary, primary.timezone);
                    const isOverlap = isBusiness && isPrimaryBusiness;
                    const isCurrentHour = correspondingHour === currentHour;
                    const isWeekendHour = isWeekend(correspondingHour, location.timezone);
                    const holidayInfo = getHolidayForHour(correspondingHour, location.timezone);

                    return (
                      <div key={primaryHour} className="hour-cell" style={{
                        padding: '1rem 0.25rem',
                        background: isOverlap ? `${location.color}${theme === 'dark' ? '30' : '40'}` : (isBusiness ? `${location.color}${theme === 'dark' ? '12' : '20'}` : 'transparent'),
                        borderLeft: isCurrentHour ? `3px solid ${location.color}` : `1px solid ${themeColors.border}`,
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: isOverlap ? `inset 0 0 20px ${location.color}${theme === 'dark' ? '20' : '30'}` : 'none',
                        minHeight: '60px'
                      }}>
                        {isCurrentHour && <div className="current-time-indicator" style={{ position: 'absolute', top: '0', left: '0', right: '0', height: '3px', background: location.color }} />}
                        {isWeekendHour && (
                          <div style={{
                            position: 'absolute',
                            top: '0.25rem',
                            right: '0.25rem',
                            background: theme === 'dark' ? 'rgba(251, 191, 36, 0.2)' : 'rgba(251, 191, 36, 0.3)',
                            color: theme === 'dark' ? '#fbbf24' : '#f59e0b',
                            padding: '0.125rem 0.25rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.5rem',
                            fontWeight: '700',
                            fontFamily: "'Space Mono', monospace",
                            letterSpacing: '0.025em'
                          }}>
                            WE
                          </div>
                        )}
                        {holidayInfo && (
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '0.25rem',
                              left: '0.25rem',
                              right: '0.25rem',
                              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                              color: 'white',
                              padding: '0.125rem 0.25rem',
                              borderRadius: '0.25rem',
                              fontSize: '0.45rem',
                              fontWeight: '700',
                              fontFamily: "'Space Mono', monospace",
                              letterSpacing: '0.025em',
                              boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                            title={holidayInfo.name}
                          >
                            🏖️ {holidayInfo.name}
                          </div>
                        )}
                        <span style={{ color: isOverlap ? themeColors.textBright : (isBusiness ? themeColors.text : themeColors.textDim), fontSize: '0.625rem', fontFamily: "'Space Mono', monospace", fontWeight: isOverlap ? '700' : (isBusiness ? '600' : '400') }}>
                          {correspondingTime.time}
                        </span>
                        <span style={{ color: isOverlap ? themeColors.textMuted : (isBusiness ? themeColors.textMuted : themeColors.textDim), fontSize: '0.5625rem', fontFamily: "'Space Mono', monospace" }}>
                          {correspondingTime.day}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            </div>
          </section>
          </>
        )}

        {showToast && (
          <div style={{
            position: 'fixed',
            bottom: '2rem',
            right: isMobile ? '1rem' : '2rem',
            left: isMobile ? '1rem' : 'auto',
            background: toastMessage.includes('removed')
              ? (theme === 'dark' ? 'rgba(251, 191, 36, 0.95)' : 'rgba(251, 191, 36, 0.9)')
              : (theme === 'dark' ? 'rgba(34, 197, 94, 0.95)' : 'rgba(34, 197, 94, 0.9)'),
            color: 'white',
            padding: '1rem 1.5rem',
            borderRadius: '0.75rem',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            fontFamily: "'Sora', sans-serif",
            fontSize: '0.875rem',
            fontWeight: '600',
            zIndex: 10000,
            animation: 'slideIn 0.3s ease-out',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.75rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span>{toastMessage.includes('removed') ? '🗑️' : '✓'}</span>
              {toastMessage}
            </div>
            {toastMessage.includes('removed') && undoStack.length > 0 && (
              <button
                onClick={() => {
                  undoRemove();
                  setShowToast(false);
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.25)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  borderRadius: '0.375rem',
                  padding: '0.375rem 0.75rem',
                  fontFamily: "'Sora', sans-serif",
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.35)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.25)'}
              >
                UNDO
              </button>
            )}
          </div>
        )}

        {showHelp && (
          <div
            className="modal-backdrop"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)',
              zIndex: 10001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: isMobile ? '1rem' : '2rem'
            }}
            onClick={() => setShowHelp(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="help-modal-title"
          >
            <div
              className="modal-content glass-panel"
              style={{
                maxWidth: '600px',
                width: '100%',
                padding: isMobile ? '1.5rem' : '2rem',
                borderRadius: '1rem',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 id="help-modal-title" style={{
                fontSize: isMobile ? '1.5rem' : '2rem',
                fontWeight: '700',
                color: themeColors.text,
                marginBottom: '1.5rem',
                fontFamily: "'Sora', sans-serif"
              }}>
                ⌨️ {t('keyboardShortcuts')}
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[
                  { key: '? or /', desc: t('showHelpDialog') },
                  { key: 'N', desc: t('addLocation') },
                  { key: 'T', desc: t('shortcuts.toggleTheme') },
                  { key: 'ESC', desc: t('closeDialogsAndCancel') },
                  { key: '↑ ↓', desc: t('navigateSearchResults') },
                  { key: 'Enter', desc: t('selectHighlightedResult') }
                ].map(({ key, desc }) => (
                  <div key={key} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1rem',
                    background: themeColors.panelBg,
                    borderRadius: '0.5rem',
                    gap: '1rem'
                  }}>
                    <kbd style={{
                      padding: '0.375rem 0.75rem',
                      background: themeColors.inputBg,
                      border: `1px solid ${themeColors.border}`,
                      borderRadius: '0.375rem',
                      fontFamily: "'Space Mono', monospace",
                      fontSize: '0.875rem',
                      color: themeColors.text,
                      fontWeight: '700',
                      minWidth: '60px',
                      textAlign: 'center'
                    }}>
                      {key}
                    </kbd>
                    <span style={{
                      color: themeColors.textMuted,
                      fontFamily: "'Sora', sans-serif",
                      fontSize: '0.875rem',
                      flex: 1
                    }}>
                      {desc}
                    </span>
                  </div>
                ))}
              </div>

              <div style={{
                marginTop: '2rem',
                padding: '1rem',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '0.5rem'
              }}>
                <p style={{
                  color: '#60a5fa',
                  fontFamily: "'Sora', sans-serif",
                  fontSize: '0.875rem',
                  margin: 0,
                  lineHeight: '1.6'
                }}>
                  💡 <strong>{t('tip')}:</strong> {t('helpTipText')}
                </p>
              </div>

              <button
                onClick={() => setShowHelp(false)}
                style={{
                  marginTop: '1.5rem',
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(59, 130, 246, 0.2)',
                  color: '#60a5fa',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '0.5rem',
                  fontFamily: "'Sora', sans-serif",
                  fontWeight: '600'
                }}
                aria-label="Close help dialog"
              >
                {t('gotIt')}
              </button>
            </div>
          </div>
        )}

        {/* Context Menu */}
        {contextMenu && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10002
            }}
            onClick={() => setContextMenu(null)}
          >
            <div
              style={{
                position: 'absolute',
                top: `${contextMenu.y}px`,
                left: `${contextMenu.x}px`,
                transform: 'translate(-50%, -100%)',
                background: themeColors.panelBgStrong,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${themeColors.border}`,
                borderRadius: '0.75rem',
                padding: '0.5rem',
                minWidth: '200px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
                animation: 'scaleIn 0.2s ease-out'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {locations.find(l => l.id === contextMenu.locationId) && (() => {
                const location = locations.find(l => l.id === contextMenu.locationId);
                return (
                  <>
                    <button
                      onClick={() => {
                        setPrimaryLocation(location.id);
                        setContextMenu(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        background: location.isPrimary ? themeColors.panelBg : 'transparent',
                        color: themeColors.text,
                        border: 'none',
                        borderRadius: '0.5rem',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontFamily: "'Sora', sans-serif",
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                      }}
                      disabled={location.isPrimary}
                    >
                      <span>⭐</span>
                      {location.isPrimary ? t('primaryLocation') : t('makePrimary')}
                    </button>
                    <button
                      onClick={() => {
                        toggleFavorite(location.timezone);
                        setContextMenu(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        background: 'transparent',
                        color: themeColors.text,
                        border: 'none',
                        borderRadius: '0.5rem',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontFamily: "'Sora', sans-serif",
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                      }}
                    >
                      <span>{favorites.includes(location.timezone) ? '⭐' : '☆'}</span>
                      {favorites.includes(location.timezone) ? t('removeFavoriteShort') : t('addFavoriteShort')}
                    </button>
                    <button
                      onClick={() => {
                        setEditingHours(location.id);
                        setContextMenu(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        background: 'transparent',
                        color: themeColors.text,
                        border: 'none',
                        borderRadius: '0.5rem',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontFamily: "'Sora', sans-serif",
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                      }}
                    >
                      <span>📋</span>
                      {t('editHours')}
                    </button>
                    <div style={{
                      height: '1px',
                      background: themeColors.border,
                      margin: '0.25rem 0'
                    }} />
                    <button
                      onClick={() => {
                        removeLocation(location.id);
                        setContextMenu(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        background: 'transparent',
                        color: '#dc2626',
                        border: 'none',
                        borderRadius: '0.5rem',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontFamily: "'Sora', sans-serif",
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                      }}
                    >
                      <span>🗑️</span>
                      {t('deleteLocation')}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Save Preset Modal */}
        {showPresetModal && (
          <div
            className="modal-backdrop"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)',
              zIndex: 10001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2rem'
            }}
            onClick={() => setShowPresetModal(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="save-preset-title"
          >
            <div
              className="modal-content glass-panel"
              style={{
                maxWidth: '500px',
                width: '100%',
                padding: '2rem',
                borderRadius: '1rem'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 id="save-preset-title" style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: themeColors.text,
                marginBottom: '1rem',
                fontFamily: "'Sora', sans-serif"
              }}>
                💾 {t('savePresetTitle')}
              </h3>
              <p style={{
                color: themeColors.textMuted,
                fontSize: '0.875rem',
                marginBottom: '1.5rem',
                fontFamily: "'Sora', sans-serif"
              }}>
                {t('savePresetDescription')}
              </p>

              <label htmlFor="preset-name-input" style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: themeColors.textMuted,
                fontSize: '0.875rem',
                fontFamily: "'Sora', sans-serif",
                fontWeight: '600'
              }}>
                {t('presetNameLabel')}
              </label>
              <input
                id="preset-name-input"
                type="text"
                placeholder={t('presetNamePlaceholder')}
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') savePreset();
                  if (e.key === 'Escape') setShowPresetModal(false);
                }}
                autoFocus
                style={{
                  width: '100%',
                  marginBottom: '1.5rem',
                  background: themeColors.inputBg,
                  color: themeColors.text,
                  border: `1px solid ${themeColors.border}`
                }}
              />

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowPresetModal(false)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: themeColors.panelBg,
                    color: themeColors.text,
                    border: `1px solid ${themeColors.border}`,
                    borderRadius: '0.5rem',
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={savePreset}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#fbbf24',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {t('savePreset')}
                </button>
              </div>

              {presets.length > 0 && (
                <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: `1px solid ${themeColors.border}` }}>
                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: themeColors.text,
                    marginBottom: '1rem',
                    fontFamily: "'Sora', sans-serif"
                  }}>
                    {t('savedPresets')}
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '200px', overflowY: 'auto' }}>
                    {presets.map(preset => (
                      <div key={preset.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.75rem',
                        background: themeColors.panelBg,
                        borderRadius: '0.5rem',
                        border: `1px solid ${themeColors.border}`
                      }}>
                        <div>
                          <div style={{
                            color: themeColors.text,
                            fontFamily: "'Sora', sans-serif",
                            fontWeight: '600',
                            fontSize: '0.875rem'
                          }}>
                            {preset.name}
                          </div>
                          <div style={{
                            color: themeColors.textMuted,
                            fontFamily: "'Space Mono', monospace",
                            fontSize: '0.75rem'
                          }}>
                            {t('locationsCount', { count: preset.locations.length })}
                          </div>
                        </div>
                        <button
                          onClick={() => deletePreset(preset.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#dc2626',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            fontSize: '1.25rem'
                          }}
                          title={t('deletePresetTitle')}
                          aria-label={`Delete preset ${preset.name}`}
                        >
                          🗑️
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Calendar Integration Modal */}
        {showCalendarModal && (
          <div
            className="modal-backdrop"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)',
              zIndex: 10001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '2rem'
            }}
            onClick={() => setShowCalendarModal(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="calendar-modal-title"
          >
            <div
              className="modal-content glass-panel"
              style={{
                maxWidth: '600px',
                width: '100%',
                padding: '2rem',
                borderRadius: '1rem',
                maxHeight: '80vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 id="calendar-modal-title" style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: themeColors.text,
                marginBottom: '1rem',
                fontFamily: "'Sora', sans-serif"
              }}>
                📅 {t('addToCalendar')}
              </h3>
              <p style={{
                color: themeColors.textMuted,
                fontSize: '0.875rem',
                marginBottom: '1.5rem',
                fontFamily: "'Sora', sans-serif"
              }}>
                {t('foundSlots', { count: bestMeetingTimes.length, slots: t(`slots_${bestMeetingTimes.length === 1 ? 'one' : 'other'}`) })}
              </p>

              <div style={{
                background: themeColors.panelBg,
                padding: '1rem',
                borderRadius: '0.5rem',
                marginBottom: '1.5rem',
                maxHeight: isMobile ? '50vh' : '60vh',
                overflowY: 'auto'
              }}>
                <h4 style={{
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: themeColors.textMuted,
                  marginBottom: '0.75rem',
                  fontFamily: "'Space Mono', monospace",
                  textTransform: 'uppercase'
                }}>
                  {t('optimalMeetingSlots')}
                </h4>
                {bestMeetingTimes.map((slot, index) => {
                  const primary = getPrimaryLocation;
                  const timeStr = slot.primaryHour.toString().padStart(2, '0') + ':00';
                  const startHour = slot.primaryHour;

                  // Create Google Calendar URL for this slot
                  const now = new Date();
                  let daysToAdd = 1;
                  const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysToAdd, startHour, 0, 0);
                  while (startDate.getDay() === 0 || startDate.getDay() === 6) {
                    daysToAdd++;
                    startDate.setDate(now.getDate() + daysToAdd);
                  }
                  const endDate = new Date(startDate.getTime() + meetingDuration * 60 * 1000);

                  const formatDateForGoogle = (date) => {
                    return date.toISOString().replace(/-|:|\.\d\d\d/g, '');
                  };

                  const title = `${t('teamMeeting', { locations: locations.map(l => l.name).join(', ') })}`;
                  const details = `${t('meetingDetails', { details: slot.details.map(d => `${d.location}: ${d.hour.toString().padStart(2, '0')}:00`).join('\\n') })}`;

                  const googleCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&details=${encodeURIComponent(details)}&dates=${formatDateForGoogle(startDate)}/${formatDateForGoogle(endDate)}`;

                  return (
                    <div key={index} style={{
                      padding: '1rem',
                      background: 'rgba(168, 85, 247, 0.05)',
                      border: '1px solid rgba(168, 85, 247, 0.3)',
                      borderRadius: '0.5rem',
                      marginBottom: '1rem'
                    }}>
                      <div style={{
                        color: '#a855f7',
                        fontFamily: "'Space Mono', monospace",
                        fontWeight: '700',
                        fontSize: '1rem',
                        marginBottom: '0.75rem'
                      }}>
                        {timeStr} ({primary?.name || 'Primary'} {t('currentTime')})
                      </div>
                      <div style={{
                        fontSize: '0.75rem',
                        color: themeColors.textMuted,
                        fontFamily: "'Sora', sans-serif",
                        marginBottom: '0.75rem'
                      }}>
                        {slot.details.map((detail, i) => (
                          <div key={i}>• {detail.location}: {detail.hour.toString().padStart(2, '0')}:00</div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexDirection: isMobile ? 'column' : 'row' }}>
                        <a
                          href={googleCalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            background: '#22c55e',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            fontFamily: "'Sora', sans-serif",
                            fontWeight: '600',
                            textAlign: 'center',
                            textDecoration: 'none',
                            fontSize: '0.875rem',
                            touchAction: 'manipulation',
                            WebkitUserSelect: 'none',
                            userSelect: 'none'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            hapticFeedback('light');
                          }}
                        >
                          📅 Google Calendar
                        </a>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            generateICSFile(slot, index);
                          }}
                          style={{
                            flex: 1,
                            padding: '0.75rem',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            fontFamily: "'Sora', sans-serif",
                            fontWeight: '600',
                            textAlign: 'center',
                            fontSize: '0.875rem',
                            cursor: 'pointer'
                          }}
                        >
                          📥 {t('downloadICS')}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', flexDirection: 'column' }}>
                <button
                  onClick={() => setShowCalendarModal(false)}
                  style={{
                    padding: '1rem',
                    background: themeColors.panelBg,
                    color: themeColors.text,
                    border: `1px solid ${themeColors.border}`,
                    borderRadius: '0.5rem',
                    fontFamily: "'Sora', sans-serif",
                    fontWeight: '600'
                  }}
                >
                  {t('close')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Bottom Sheet for Add Location */}
        {showAdd && isMobile && (
          <div
            className="modal-backdrop"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 10001,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              padding: '1rem',
              paddingTop: '15vh',
              overflowY: 'auto'
            }}
            onClick={() => { setShowAdd(false); setSearchQuery(''); }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-location-title"
          >
            <div
              className="glass-panel"
              style={{
                width: '100%',
                maxWidth: '500px',
                maxHeight: '70vh',
                height: 'auto',
                padding: '1rem',
                borderRadius: '1rem',
                overflowY: 'hidden',
                WebkitOverflowScrolling: 'touch',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <h3 id="add-location-title" style={{
                  fontSize: '1.125rem',
                  fontWeight: '700',
                  color: themeColors.text,
                  margin: 0,
                  fontFamily: "'Sora', sans-serif"
                }}>
                  Add Location
                </h3>
                <button
                  onClick={() => { setShowAdd(false); setSearchQuery(''); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: themeColors.text,
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    padding: '0.5rem',
                    minWidth: '44px',
                    minHeight: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    touchAction: 'manipulation',
                    marginRight: '-0.5rem'
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div style={{ width: '100%', position: 'relative', flexShrink: 0 }}>
                <div style={{ position: 'relative' }}>
                  <Search size={18} style={{
                    position: 'absolute',
                    left: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: themeColors.textDim,
                    pointerEvents: 'none',
                    zIndex: 1
                  }} />
                  <input
                    id="city-search-mobile"
                    type="text"
                    placeholder={t('search')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    autoComplete="off"
                    aria-label="Search for a city, country, or timezone"
                    role="combobox"
                    aria-expanded={filteredCities.length > 0}
                    aria-controls="city-results-mobile"
                    aria-activedescendant={filteredCities.length > 0 ? `city-option-mobile-${selectedIndex}` : undefined}
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem 0.875rem 3rem',
                      fontSize: '1rem',
                      background: themeColors.inputBg,
                      color: themeColors.text,
                      border: `2px solid ${themeColors.border}`,
                      borderRadius: '0.5rem',
                      touchAction: 'manipulation',
                      WebkitAppearance: 'none'
                    }}
                  />
                </div>
              </div>

              {/* Scrollable content area */}
              <div style={{
                flexGrow: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                minHeight: 0 // Important for flexbox scrolling
              }}>
                {searchQuery.trim() === '' && (
                  <div>
                    {favorites.length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{
                          fontSize: '0.6875rem',
                          fontFamily: "'Space Mono', monospace",
                          color: themeColors.textMuted,
                          marginBottom: '0.5rem',
                          fontWeight: '700'
                        }}>
                          ⭐ FAVORITES
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {favorites.map(timezone => {
                            const cityName = Object.keys(cityTimezoneMap).find(
                              city => cityTimezoneMap[city] === timezone
                            );
                            return cityName ? (
                              <button
                                key={timezone}
                                onClick={() => addLocationFromCity(cityName, timezone)}
                                style={{
                                  padding: '0.625rem 0.875rem',
                                  background: 'rgba(251, 191, 36, 0.1)',
                                  color: '#fbbf24',
                                  border: '1px solid rgba(251, 191, 36, 0.3)',
                                  borderRadius: '0.5rem',
                                  fontFamily: "'Sora', sans-serif",
                                  fontSize: '0.8125rem',
                                  cursor: 'pointer',
                                  minHeight: '44px',
                                  touchAction: 'manipulation'
                                }}
                                title={`Add ${cityName}`}
                              >
                                ⭐ {cityName}
                              </button>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}

                    {userLocationSuggestion && (
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{
                          fontSize: '0.6875rem',
                          fontFamily: "'Space Mono', monospace",
                          color: themeColors.textMuted,
                          marginBottom: '0.5rem',
                          fontWeight: '700'
                        }}>
                          📍 YOUR LOCATION
                        </div>
                        <button
                          onClick={() => {
                            addLocationFromCity(userLocationSuggestion.city, userLocationSuggestion.timezone);
                            setShowAdd(false);
                            setSearchQuery('');
                          }}
                          style={{
                            padding: '0.625rem 0.875rem',
                            background: 'rgba(34, 197, 94, 0.1)',
                            color: '#22c55e',
                            border: '1px solid rgba(34, 197, 94, 0.3)',
                            borderRadius: '0.5rem',
                            fontFamily: "'Sora', sans-serif",
                            fontSize: '0.8125rem',
                            cursor: 'pointer',
                            fontWeight: '600',
                            minHeight: '44px',
                            touchAction: 'manipulation'
                          }}
                          title={`Add ${userLocationSuggestion.city} (Your current timezone)`}
                        >
                          📍 {userLocationSuggestion.city}
                        </button>
                      </div>
                    )}

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{
                        fontSize: '0.6875rem',
                        fontFamily: "'Space Mono', monospace",
                        color: themeColors.textMuted,
                        marginBottom: '0.5rem',
                        fontWeight: '700'
                      }}>
                        🌍 POPULAR LOCATIONS
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {[
                          { city: 'New York', tz: 'America/New_York', abbrev: 'EST' },
                          { city: 'London', tz: 'Europe/London', abbrev: 'GMT' },
                          { city: 'Tokyo', tz: 'Asia/Tokyo', abbrev: 'JST' },
                          { city: 'Sydney', tz: 'Australia/Sydney', abbrev: 'AEST' },
                          { city: 'Los Angeles', tz: 'America/Los_Angeles', abbrev: 'PST' },
                          { city: 'Singapore', tz: 'Asia/Singapore', abbrev: 'SGT' },
                          { city: 'Dubai', tz: 'Asia/Dubai', abbrev: 'GST' },
                          { city: 'Mumbai', tz: 'Asia/Kolkata', abbrev: 'IST' }
                        ].map(({ city, tz, abbrev }) => (
                          <button
                            key={city}
                            onClick={() => addLocationFromCity(city, tz)}
                            style={{
                              padding: '0.625rem 0.875rem',
                              background: 'rgba(59, 130, 246, 0.1)',
                              color: '#60a5fa',
                              border: '1px solid rgba(59, 130, 246, 0.3)',
                              borderRadius: '0.5rem',
                              fontFamily: "'Sora', sans-serif",
                              fontSize: '0.8125rem',
                              cursor: 'pointer',
                              minHeight: '44px',
                              touchAction: 'manipulation'
                            }}
                            title={`Add ${city} (${abbrev})`}
                          >
                            {city}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {filteredCities.length > 0 && (
                  <div
                    id="city-results-mobile"
                    role="listbox"
                    aria-label="Search results"
                    style={{
                      background: themeColors.panelBgStrong,
                      border: `1px solid ${themeColors.border}`,
                      borderRadius: '0.5rem',
                      overflow: 'hidden'
                    }}
                  >
                    {filteredCities.map((city, index) => {
                      const matchTypeBadge = {
                        timezone: { label: 'TZ', color: '#8b5cf6' },
                        country: { label: 'Country', color: '#10b981' },
                        city: { label: 'City', color: '#3b82f6' }
                      };
                      const badge = matchTypeBadge[city.matchType] || matchTypeBadge.city;

                      return (
                        <div
                          key={`${city.name}-${index}`}
                          id={`city-option-mobile-${index}`}
                          role="option"
                          aria-selected={index === selectedIndex}
                          onClick={() => addLocationFromCity(city.name, city.timezone)}
                          style={{
                            padding: '1rem',
                            cursor: 'pointer',
                            background: index === selectedIndex ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                            borderBottom: index < filteredCities.length - 1 ? `1px solid ${themeColors.border}` : 'none',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '0.75rem',
                            minHeight: '64px',
                            touchAction: 'manipulation'
                          }}
                          onTouchStart={() => setSelectedIndex(index)}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{
                              color: themeColors.text,
                              fontFamily: "'Sora', sans-serif",
                              fontWeight: '600',
                              marginBottom: '0.25rem',
                              fontSize: '1rem'
                            }}>
                              {city.name}
                            </div>
                            <div style={{
                              color: themeColors.textMuted,
                              fontFamily: "'Space Mono', monospace",
                              fontSize: '0.8125rem'
                            }}>
                              {city.timezone}
                            </div>
                          </div>
                          {city.matchType && (
                            <span style={{
                              padding: '0.375rem 0.625rem',
                              background: `${badge.color}20`,
                              color: badge.color,
                              borderRadius: '0.375rem',
                              fontSize: '0.6875rem',
                              fontWeight: '700',
                              fontFamily: "'Sora', sans-serif",
                              textTransform: 'uppercase'
                            }}>
                              {badge.label}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bottom Ad Banner */}
        {locations.length > 0 && (
          <AdBanner
            type="adsense"
            slot="9876543210"
            format="horizontal"
            show={showBottomAd}
            onClose={() => setShowBottomAd(false)}
            isMobile={isMobile}
            theme={theme}
            themeColors={themeColors}
          />
        )}

        {/* Footer */}
        <footer style={{
          width: '100%',
          padding: isMobile ? '1rem 1rem 2rem' : '1.5rem 2rem 3rem',
          background: theme === 'dark' ? 'rgba(15, 23, 42, 0.5)' : 'rgba(255, 255, 255, 0.5)',
          borderTop: `1px solid ${themeColors.border}`,
          textAlign: 'center'
        }}>
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            alignItems: 'center'
          }}>
            {/* Clockbridge Logo Small */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <svg width={24} height={24} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="20" stroke="#60a5fa" strokeWidth="2.5" fill="none" opacity="0.8"/>
                <line x1="24" y1="24" x2="24" y2="12" stroke="#60a5fa" strokeWidth="2.5" strokeLinecap="round"/>
                <line x1="24" y1="24" x2="32" y2="24" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" opacity="0.7"/>
                <path d="M 8 24 Q 24 8, 40 24" stroke="#60a5fa" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.6"/>
                <line x1="12" y1="24" x2="12" y2="32" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
                <line x1="24" y1="16" x2="24" y2="32" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
                <line x1="36" y1="24" x2="36" y2="32" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
                <circle cx="24" cy="24" r="2" fill="#60a5fa"/>
              </svg>
              <span style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: themeColors.text,
                fontFamily: "'Sora', sans-serif"
              }}>
                clockbridge
              </span>
            </div>

            {/* Copyright and Attribution */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '0.5rem' : '1.5rem',
              alignItems: 'center',
              fontSize: '0.875rem',
              color: themeColors.textMuted,
              fontFamily: "'Sora', sans-serif"
            }}>
              <span>© 2026 Clockbridge. All rights reserved.</span>
              <span style={{ display: isMobile ? 'none' : 'inline' }}>•</span>
              <a
                href="https://launchp.ad"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#60a5fa',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#3b82f6';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#60a5fa';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                Built with <span style={{ color: '#ef4444', fontSize: '1rem' }}>♥</span> by Launchp.ad
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default TimeZoneVisualizer;

// Mount React app for production
if (typeof window !== 'undefined' && document.getElementById('root')) {
  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <TimeZoneVisualizer />
    </React.StrictMode>
  );
}
